import { LoadingState } from '../ui';
// src/pages/CompetitionView.jsx

import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { subscribeToChanges } from '../lib/realtimeSubscriptions';
import analytics from '../lib/analytics';
import notify from '../lib/notify';
import { isAtLeastAge } from "../lib/ageUtils";
import SidebarNavbar from "../components/SidebarNavbar";
import PontoonView from "../components/PontoonView";
import { PONTOON_ENABLED } from "../lib/featureFlags";
import BottomNavbar from "../components/BottomNavbar";
import HowToPlayModal from "../components/HowToPlayModal";
import ShareButton from '../components/ShareButton';
import TopGafferConfig from "../components/TopGafferConfig";
import { Button as FcButton, Card as FcCard, Input as FcInput, LinkButton as FcLinkButton, Select as FcSelect, Table as FcTable } from '../ui';
import { Dialog as FcDialog } from '../ui';
import { Tabs as FcTabs } from '../ui';
import { LastChanceChips } from '../ui';
import {EntrySelector, SegmentedControl} from '../ui';
import CompetitionCardHeader from '../components/CompetitionCardHeader';
import {getCompetitionHeaderBrand} from '../lib/competitionHeaderBrand';

export default function CompetitionView({ session, userRole, username }) {
  const isAlreadyRegisteredError = (error) => {
    if (!error) return false;
    return error.code === "23505" || (error.message || "").toLowerCase().includes("duplicate key");
  };

  // Logout handler for navbars
  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = "/login";
  };
  const { competitionId } = useParams();
  const [competition, setCompetition] = useState(null);
  const [leagueMedia, setLeagueMedia] = useState(null);
  useEffect(()=>{
    let current=true;
    setLeagueMedia(null);
    if(!competition || !getCompetitionHeaderBrand(competition).known)return;
    // Optional existing media only; never changes competition or permission reads.
    Promise.resolve(supabase.from('api_football_leagues').select('logo, flag').eq('league_name',competition.league).limit(1).maybeSingle())
      .then(({data,error})=>{if(current && !error)setLeagueMedia({league:competition.league,...data});})
      .catch(()=>{});
    return ()=>{current=false;};
  },[competition?.league, competition?.is_multi_league]);
  // Phase 2: this user's role for the competition, and the admin roster.
  // Declared early because the Top Gaffer effect below reads myCompRole.
  const [myCompRole, setMyCompRole] = useState(null); // 'owner' | 'admin' | null
  const [compAdmins, setCompAdmins] = useState([]);    // [{ user_id, role, username, added_by, created_at }]
  const [fixtures, setFixtures] = useState([]);
  const [teamSelection, setTeamSelection] = useState(null);
  const [matchdays, setMatchdays] = useState([]);
  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registeredPlayers, setRegisteredPlayers] = useState([]); // [{username, current_selection, status, ...}]
  // Pagination for Registered Players table
  const [regPlayersPage, setRegPlayersPage] = useState(1);
  const REG_PLAYERS_PER_PAGE = 50;
  const [standings, setStandings] = useState([]); // for season or knockout
  const [activeTab, setActiveTab] = useState("overview");
  const [isRegistered, setIsRegistered] = useState(false);
  const [winnerName, setWinnerName] = useState(null);
  const [mySelection, setMySelection] = useState(null);
  const [showHowModal, setShowHowModal] = useState(false);
  // Private join modal state (match Competitions.jsx)
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [keyError, setKeyError] = useState("");
  const [pendingComp, setPendingComp] = useState(null);
  const [standingsPage, setStandingsPage] = useState(1);
  const [topGafferStages, setTopGafferStages] = useState([]);
  const [selectedStageId, setSelectedStageId] = useState(null);
  const [stageStandings, setStageStandings] = useState([]);
  const [tgManaging, setTgManaging] = useState(false);
  const [tgAvailable, setTgAvailable] = useState([]);
  const [tgEditValue, setTgEditValue] = useState({ enabled: false, stageCount: 1, stages: [] });
  const [tgSaving, setTgSaving] = useState(false);
  const STANDINGS_PER_PAGE = 50;

  // Load Top Gaffer stages when viewing an enabled Season competition.
  useEffect(() => {
    if (competition?.type === 'season' && competition?.top_gaffer_enabled) {
      supabase
        .rpc('get_top_gaffer_stages', { p_competition_id: competitionId })
        .then(({ data }) => setTopGafferStages(data || []));
    } else {
      setTopGafferStages([]);
      setSelectedStageId(null);
    }
  }, [competition?.type, competition?.top_gaffer_enabled, competitionId]);

  // Load stage standings when a Top Gaffer stage is selected (Full Season uses the existing view).
  useEffect(() => {
    if (!selectedStageId) { setStageStandings([]); return; }
    const stage = topGafferStages.find((s) => s.stage_id === selectedStageId);
    if (stage?.status === 'upcoming') { setStageStandings([]); return; }
    supabase
      .rpc('get_top_gaffer_stage_standings', { p_competition_id: competitionId, p_stage_id: selectedStageId })
      .then(({ data }) => {
        setStageStandings(
          (data || []).map((r) => ({
            user_id: r.user_id,
            username: r.username,
            rank: r.rank,
            wins: r.wins,
            draws: r.draws,
            losses: r.losses,
            gf: r.goals_for,
            ga: r.goals_against,
            points: r.points,
          }))
        );
      });
  }, [selectedStageId, competitionId, topGafferStages]);

  // Load available matchdays for editing Top Gaffer (creator/admin on a Season competition).
  useEffect(() => {
    if (!competition || competition.type !== 'season') return;
    const canManage = userRole === 'admin' || competition.created_by === session?.user?.id || myCompRole === 'owner' || myCompRole === 'admin';
    if (!canManage) return;
    supabase
      .from('matchdays_view')
      .select('match_day, first_kickoff_time')
      .eq('league', competition.league)
      .eq('season', competition.season)
      .order('match_day', { ascending: true })
      .then(({ data }) => setTgAvailable(data || []));
  }, [competition?.league, competition?.season, competition?.type, competition?.created_by, userRole, session?.user?.id, myCompRole]);
  const [selectionHistory, setSelectionHistory] = useState([]);
  const [matchDay, setMatchDay] = useState(1);
  // Fixtures view toggle and team selection
  const [fixturesViewMode, setFixturesViewMode] = useState("matchday"); // "matchday" or "team"
  const [selectedTeam, setSelectedTeam] = useState("");
  const [availableTeams, setAvailableTeams] = useState([]);
  const [manageEntryId, setManageEntryId] = useState("");
  const [manageTeam, setManageTeam] = useState(availableTeams[0] || "");
  
  // Squad invitation modal state
  const [showInviteSquadModal, setShowInviteSquadModal] = useState(false);
  const [userSquads, setUserSquads] = useState([]);
  const [selectedSquadsToInvite, setSelectedSquadsToInvite] = useState([]);
  const [sendingInvites, setSendingInvites] = useState(false);
  const [manageLoading, setManageLoading] = useState(false);
  const [manageError, setManageError] = useState("");
  const [manageSuccess, setManageSuccess] = useState("");
  // Team dropdown state
  const [teamDropdownOpen, setTeamDropdownOpen] = useState(false);
  const teamDropdownRef = useRef(null);
  // Last Chance Chips state
  const [chipsRemaining, setChipsRemaining] = useState(0);
  const [chipBalance, setChipBalance] = useState(null);
  // Postponement Credits state (Season competitions)
  const [postponementCredits, setPostponementCredits] = useState(0);
  // Must Pick Double state (Knockout competitions)
  const [mustPickDouble, setMustPickDouble] = useState(false);
  // Remaining teams to select
  const [remainingTeams, setRemainingTeams] = useState([]);
  const [showRemainingTeams, setShowRemainingTeams] = useState(false);
  // Multi-entry: the current user's entries in this competition, and which one is
  // active for the Overview / My Selections tabs.
  const [myEntries, setMyEntries] = useState([]); // [{ id, entry_index, entry_label }]
  const [activeEntryId, setActiveEntryId] = useState(null);
  const [addingEntry, setAddingEntry] = useState(false);
  const [showUnregisterModal, setShowUnregisterModal] = useState(false);
  const [unregisterSelectedIds, setUnregisterSelectedIds] = useState([]);
  const [unregistering, setUnregistering] = useState(false);
  const [newAdminUserId, setNewAdminUserId] = useState("");
  const [adminActionBusy, setAdminActionBusy] = useState(false);
  const [adminError, setAdminError] = useState("");
  // Phase 3: entry management roster (all entries incl. pending) + busy flags.
  const [manageEntries, setManageEntries] = useState([]);
  const [entryActionBusy, setEntryActionBusy] = useState(false);
  const [requiresApprovalBusy, setRequiresApprovalBusy] = useState(false);
  const [entriesConfirmedFilter, setEntriesConfirmedFilter] = useState('all'); // 'all' | 'confirmed' | 'unconfirmed'
  const [pendingEntriesCount, setPendingEntriesCount] = useState(0);
  // Phase 5: multi-league round editor (edit gameweeks + upcoming matchday mappings).
  const [roundEditor, setRoundEditor] = useState({ leagues: [], season: '', rounds: [] }); // rounds: [{[league]:matchday}]
  const [leagueMatchdays, setLeagueMatchdays] = useState({}); // league -> [{match_day, first_kickoff_time}]
  const [roundEditorBusy, setRoundEditorBusy] = useState(false);
  const [roundEditorLoaded, setRoundEditorLoaded] = useState(false);
  const [showRemoveRoundsModal, setShowRemoveRoundsModal] = useState(false);
  const [removeRoundsSelected, setRemoveRoundsSelected] = useState(() => new Set());
  // Multi-league summary for the Overview (visible to all viewers, not just managers).
  const [mlSummary, setMlSummary] = useState(null); // { leagues:[], season, rounds:[{[league]:match_day}] }
  // Linked Pontoon side-game (if this Knockout has one), for an Overview banner.
  const [linkedPontoon, setLinkedPontoon] = useState(null); // { id, name } | null

  // Fetch a specific ENTRY's selection history for this competition.
  // entryId identifies which of the user's entries to load (Overview / My Selections
  // are scoped to the active entry). Falls back to the user's first entry.
  const fetchSelectionHistory = async (compData, entryId = null) => {
    setChipBalance(null);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    try {
      // Resolve the entry to load (default: the user's first entry).
      let resolvedEntryId = entryId;
      if (!resolvedEntryId) {
        const { data: firstEntry } = await supabase
          .from('user_selections')
          .select('id')
          .eq('user_id', user.id)
          .eq('competition_id', competitionId)
          .order('entry_index', { ascending: true })
          .limit(1)
          .maybeSingle();
        resolvedEntryId = firstEntry?.id || null;
      }
      if (!resolvedEntryId) {
        setSelectionHistory([]);
        setMySelection(null);
        return;
      }

      // Fetch this entry's chips / flags.
      const { data: userSelData } = await supabase
        .from('user_selections')
        .select('last_chance_chips_remaining, postponement_credits, must_pick_double')
        .eq('id', resolvedEntryId)
        .maybeSingle();

      if (userSelData) {
        setChipBalance(Number.isSafeInteger(userSelData.last_chance_chips_remaining) && userSelData.last_chance_chips_remaining >= 0 ? userSelData.last_chance_chips_remaining : null);
        setChipsRemaining(userSelData.last_chance_chips_remaining || 0);
        setPostponementCredits(userSelData.postponement_credits || 0);
        setMustPickDouble(userSelData.must_pick_double || false);
      }

      // Get the team selections for THIS ENTRY
      const { data: selections, error: selectionsError } = await supabase
        .from('team_selections')
        .select('*, used_last_chance_chip')
        .eq('entry_id', resolvedEntryId)
        .order('created_at', { ascending: true });

      if (selectionsError) {
        return;
      }

      // Set the Overview "Current Selection" from this entry's current-matchday pick(s).
      const currentMdTeams = (selections || [])
        .filter((s) => s.match_day === compData.current_match_day)
        .map((s) => s.selected_team);
      setMySelection(currentMdTeams.length ? currentMdTeams.join(', ') : null);

      if (!selections || selections.length === 0) {
        setSelectionHistory([]);
        return;
      }

      // Get unique fixture IDs from selections
      const fixtureIds = [...new Set(selections.map(s => s.fixture_id).filter(Boolean))];
      
      if (fixtureIds.length === 0) {
        setSelectionHistory(selections.map(s => ({ ...s, fixtures: null })));
        return;
      }

      // Separately fetch fixture details
      const { data: fixtures, error: fixturesError } = await supabase
        .from('fixtures')
        .select('*')
        .in('id', fixtureIds);

      if (fixturesError) {
        setSelectionHistory(selections.map(s => ({ ...s, fixtures: null })));
        return;
      }

      // Manually join the data
      const enrichedSelections = selections.map(selection => {
        const fixture = fixtures?.find(f => f.id === selection.fixture_id);
        return {
          ...selection,
          fixtures: fixture || null
        };
      });

      setSelectionHistory(enrichedSelections);
      
      // Calculate remaining teams (teams that can still be selected)
      if (compData) {
        const { data: fixturesData } = await supabase
          .from('fixtures')
          .select('home_team, away_team')
          .eq('league', compData.league)
          .eq('season', compData.season);
        
        if (fixturesData) {
          const allTeams = new Set();
          fixturesData.forEach(f => {
            if (f.home_team) allTeams.add(f.home_team);
            if (f.away_team) allTeams.add(f.away_team);
          });
          
          // Count how many times each team has been selected
          const teamSelectionCount = {};
          enrichedSelections.forEach(s => {
            if (s.selected_team) {
              teamSelectionCount[s.selected_team] = (teamSelectionCount[s.selected_team] || 0) + 1;
            }
          });
          
          // For knockout: show teams never selected
          // For season/scout: show teams never selected on first cycle,
          // then show teams selected < 2 times once user starts second cycle
          let remaining;
          if (compData.type === 'knockout') {
            // Knockout: only show teams never selected
            remaining = Array.from(allTeams)
              .filter(team => (teamSelectionCount[team] || 0) === 0)
              .sort();
          } else {
            // Season/Scout: check if user has entered second cycle
            const hasSecondCycleSelections = Object.values(teamSelectionCount).some(count => count >= 2);
            
            if (hasSecondCycleSelections) {
              // Second cycle: show teams selected less than 2 times
              remaining = Array.from(allTeams)
                .filter(team => (teamSelectionCount[team] || 0) < 2)
                .sort();
            } else {
              // First cycle: only show teams never selected
              remaining = Array.from(allTeams)
                .filter(team => (teamSelectionCount[team] || 0) === 0)
                .sort();
            }
          }
          
          setRemainingTeams(remaining);
        }
      }

    } catch (e) {
    }
  };

  // Phase 2: load the competition's admin roster (with usernames) and return the
  // signed-in user's role for it ('owner' via created_by, 'admin' via a row, else null).
  const loadCompAdmins = async (compData, userId) => {
    const { data: adminRows } = await supabase
      .from('competition_admins')
      .select('user_id, role, added_by, created_at')
      .eq('competition_id', competitionId);
    const admins = adminRows || [];
    const ids = [...new Set(admins.map((a) => a.user_id).filter(Boolean))];
    let names = {};
    if (ids.length) {
      const { data: profs } = await supabase.from('profiles').select('id, username').in('id', ids);
      (profs || []).forEach((p) => { names[p.id] = p.username; });
    }
    setCompAdmins(admins.map((a) => ({ ...a, username: names[a.user_id] || 'Unknown' })));
    const role = compData?.created_by === userId
      ? 'owner'
      : (admins.some((a) => a.user_id === userId && a.role === 'admin') ? 'admin' : null);
    setMyCompRole(role);
    return role;
  };

  // Phase 3: load the full entry roster (including pending/rejected) for the
  // Manage Entries tab, with usernames.
  const loadManageEntries = async () => {
    const { data: rows } = await supabase
      .from('user_selections')
      .select('id, user_id, entry_index, entry_label, status, confirmed, created_at')
      .eq('competition_id', competitionId)
      .order('created_at', { ascending: true });
    const list = rows || [];
    const ids = [...new Set(list.map((r) => r.user_id).filter(Boolean))];
    let names = {};
    if (ids.length) {
      const { data: profs } = await supabase.from('profiles').select('id, username').in('id', ids);
      (profs || []).forEach((p) => { names[p.id] = p.username; });
    }
    setManageEntries(list.map((r) => ({ ...r, username: names[r.user_id] || 'Unknown' })));
  };

  const setEntryStatus = async (entryId, status) => {
    setEntryActionBusy(true);
    try {
      const { error } = await supabase
        .from('user_selections')
        .update({ status })
        .eq('id', entryId);
      if (error) { notify(error.message || 'Failed to update entry.', 'error'); return; }
      await loadManageEntries();
    } finally {
      setEntryActionBusy(false);
    }
  };

  const handleApproveEntry = (entryId) => setEntryStatus(entryId, 'active');
  const handleRejectEntry = (entryId) => setEntryStatus(entryId, 'rejected');

  const handleRemoveEntry = async (entryId) => {
    setEntryActionBusy(true);
    try {
      const { error } = await supabase
        .from('user_selections')
        .delete()
        .eq('id', entryId);
      if (error) { notify(error.message || 'Failed to remove entry.', 'error'); return; }
      notify('Entry removed.', 'success');
      await loadManageEntries();
    } finally {
      setEntryActionBusy(false);
    }
  };

  const handleToggleConfirmed = async (entryId, confirmed) => {
    setEntryActionBusy(true);
    try {
      const { error } = await supabase
        .from('user_selections')
        .update({ confirmed })
        .eq('id', entryId);
      if (error) { notify(error.message || 'Failed to update.', 'error'); return; }
      await loadManageEntries();
    } finally {
      setEntryActionBusy(false);
    }
  };

  const handleToggleRequiresApproval = async (value) => {
    if (!competition) return;
    setRequiresApprovalBusy(true);
    try {
      const { error } = await supabase
        .from('competitions')
        .update({ requires_approval: value })
        .eq('id', competition.id);
      if (error) { notify(error.message || 'Failed to update setting.', 'error'); return; }
      setCompetition((prev) => (prev ? { ...prev, requires_approval: value } : prev));
      notify(value ? 'New entries now require approval.' : 'New entries join instantly.', 'success');
    } finally {
      setRequiresApprovalBusy(false);
    }
  };

  // Phase 5: load the multi-league round mapping into the editor (+ each league's matchdays).
  const loadRoundEditor = async () => {
    const { data: rows } = await supabase
      .from('competition_round_matchdays')
      .select('round_number, league, season, match_day')
      .eq('competition_id', competitionId)
      .order('round_number', { ascending: true });
    const list = rows || [];
    const leagues = [...new Set(list.map((r) => r.league))];
    const season = list[0]?.season || competition?.season || '';
    const maxRound = list.reduce((m, r) => Math.max(m, r.round_number), 0);
    const rounds = [];
    for (let i = 1; i <= maxRound; i++) rounds.push({});
    list.forEach((r) => { rounds[r.round_number - 1][r.league] = r.match_day; });
    setRoundEditor({ leagues, season, rounds });

    const md = {};
    for (const lg of leagues) {
      const { data } = await supabase
        .from('matchdays_view')
        .select('match_day, first_kickoff_time')
        .eq('league', lg)
        .eq('season', season)
        .order('match_day', { ascending: true });
      md[lg] = data || [];
    }
    setLeagueMatchdays(md);
    setRoundEditorLoaded(true);
  };

  const registrationOpen = competition?.registration_status === 'open';
  const editorCurrentRound = competition?.current_match_day || 1;
  const isRoundEditable = (idx) => registrationOpen || (idx + 1) > editorCurrentRound;

  const setEditorNumRounds = (n) => {
    if (!registrationOpen) return; // gameweek count locks once registration closes
    const count = Math.max(1, Math.min(60, Number(n) || 0));
    // Reductions go through the "Remove rounds" modal so the user picks which rounds
    // to drop (pre-selecting the last ones to match the arrow-down intent).
    if (count < roundEditor.rounds.length) {
      const dropCount = roundEditor.rounds.length - count;
      const preset = new Set();
      for (let i = roundEditor.rounds.length - dropCount; i < roundEditor.rounds.length; i++) preset.add(i);
      setRemoveRoundsSelected(preset);
      setShowRemoveRoundsModal(true);
      return; // input snaps back; the modal applies the change
    }
    setRoundEditor((prev) => {
      let rounds = prev.rounds.slice(0, count);
      while (rounds.length < count) rounds.push({});
      return { ...prev, rounds };
    });
  };

  const editorRoundSummary = (roundMap) => {
    const parts = Object.entries(roundMap || {})
      .filter(([, md]) => md != null && md !== '')
      .map(([lg, md]) => `${lg} MD${md}`);
    return parts.length ? parts.join(', ') : 'No selections';
  };

  const toggleRemoveRound = (idx) => {
    setRemoveRoundsSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const applyRemoveRounds = () => {
    setRoundEditor((prev) => {
      const rounds = prev.rounds.filter((_, i) => !removeRoundsSelected.has(i));
      if (rounds.length < 1) return prev; // keep at least one round
      return { ...prev, rounds };
    });
    setShowRemoveRoundsModal(false);
    setRemoveRoundsSelected(new Set());
  };

  // Lowest matchday a league may use in a given round: strictly greater than the
  // latest matchday chosen for that league in an EARLIER round. Independent per league.
  const editorMinMatchday = (idx, lg) => {
    let floor = 0;
    for (let i = 0; i < idx; i++) {
      const v = roundEditor.rounds[i]?.[lg];
      if (v != null && v > floor) floor = v;
    }
    return floor;
  };

  const setEditorRoundMatchday = (idx, lg, matchDay) => {
    const val = matchDay === '' || matchDay == null ? null : Number(matchDay);
    setRoundEditor((prev) => {
      const rounds = prev.rounds.map((r, i) => {
        if (i === idx) {
          const copy = { ...r };
          if (val == null) delete copy[lg];
          else copy[lg] = val;
          return copy;
        }
        // Clear this league in later editable rounds if the pick makes them non-increasing.
        if (i > idx && val != null && r[lg] != null && r[lg] <= val && isRoundEditable(i)) {
          const copy = { ...r };
          delete copy[lg];
          return copy;
        }
        return r;
      });
      return { ...prev, rounds };
    });
  };

  const saveRoundEditor = async () => {
    if (roundEditorBusy) return;
    // Validate: every editable round must have at least one league mapped.
    const badRound = roundEditor.rounds.findIndex(
      (r, idx) => isRoundEditable(idx) && (!r || Object.keys(r).length === 0)
    );
    if (badRound !== -1) {
      notify(`Round ${badRound + 1} needs at least one league's matchday.`, 'info');
      return;
    }
    setRoundEditorBusy(true);
    try {
      // Registration open: replace the whole mapping. Otherwise: only rounds
      // beyond the current one (upcoming) may change.
      let del = supabase.from('competition_round_matchdays').delete().eq('competition_id', competition.id);
      if (!registrationOpen) del = del.gt('round_number', editorCurrentRound);
      const { error: delErr } = await del;
      if (delErr) { notify(delErr.message || 'Failed to update match days.', 'error'); return; }

      const rows = [];
      roundEditor.rounds.forEach((rmap, idx) => {
        if (!isRoundEditable(idx)) return; // leave locked rounds untouched
        Object.entries(rmap || {}).forEach(([lg, mdv]) => {
          if (mdv != null && mdv !== '') {
            rows.push({
              competition_id: competition.id,
              round_number: idx + 1,
              league: lg,
              season: roundEditor.season || competition.season,
              match_day: Number(mdv),
            });
          }
        });
      });
      if (rows.length > 0) {
        const { error: insErr } = await supabase.from('competition_round_matchdays').insert(rows);
        if (insErr) { notify(insErr.message || 'Failed to save match days.', 'error'); return; }
      }
      notify('Match days updated.', 'success');
      await loadRoundEditor();
    } finally {
      setRoundEditorBusy(false);
    }
  };

  // Fetch user's squads for invitation
  const fetchUserSquads = async () => {
    if (!session?.user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('squad_members')
        .select(`
          squad_id,
          squads (
            id,
            name
          )
        `)
        .eq('user_id', session.user.id);

      if (error) throw error;
      
      const squads = data?.map(item => ({
        id: item.squads.id,
        name: item.squads.name
      })) || [];
      
      setUserSquads(squads);
    } catch (error) {
    }
  };

  // Handle squad invitation submission
  const handleSendSquadInvitations = async () => {
    if (selectedSquadsToInvite.length === 0) {
      notify('Please select at least one squad', 'warning');
      return;
    }

    setSendingInvites(true);
    try {
      // Get all squad members from selected squads
      const { data: squadMembers, error: membersError } = await supabase
        .from('squad_members')
        .select('user_id')
        .in('squad_id', selectedSquadsToInvite);

      if (membersError) throw membersError;

      // Get unique user IDs (excluding the current user)
      const uniqueUserIds = [...new Set(
        squadMembers?.map(m => m.user_id).filter(id => id !== session.user.id) || []
      )];

      if (uniqueUserIds.length === 0) {
        notify('No squad members to invite', 'info');
        setSendingInvites(false);
        return;
      }

      // Check for existing invitations to avoid duplicates
      const { data: existingInvites } = await supabase
        .from('competition_invitations')
        .select('invited_user_id')
        .eq('competition_id', competitionId)
        .in('invited_user_id', uniqueUserIds);

      const existingUserIds = new Set(existingInvites?.map(inv => inv.invited_user_id) || []);
      const usersToInvite = uniqueUserIds.filter(id => !existingUserIds.has(id));

      if (usersToInvite.length === 0) {
        notify('All squad members have already been invited', 'info');
        setShowInviteSquadModal(false);
        setSelectedSquadsToInvite([]);
        setSendingInvites(false);
        return;
      }

      // Create invitation records for users who don't already have invitations
      const invitations = usersToInvite.map(userId => ({
        competition_id: competitionId,
        invited_user_id: userId,
        invited_by: session.user.id,
        status: 'pending'
      }));

      const { error: inviteError } = await supabase
        .from('competition_invitations')
        .insert(invitations);

      if (inviteError) {
        throw inviteError;
      }

      const skippedCount = uniqueUserIds.length - usersToInvite.length;
      if (skippedCount > 0) {
        notify(`Sent ${usersToInvite.length} new invitation(s)! (${skippedCount} already invited)`, 'success');
      } else {
        notify(`Successfully sent ${usersToInvite.length} invitation(s)!`, 'success');
      }

      setShowInviteSquadModal(false);
      setSelectedSquadsToInvite([]);
    } catch (error) {
      notify('Failed to send invitations', 'error');
    } finally {
      setSendingInvites(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch competition data along with the creator's username for all competition types
      const { data: competitionData } = await supabase
        .from("competitions")
        .select("id, name, league, season, start_match_day, current_match_day, type, status, registration_status, knockout_winner, knockout_winner_entry_id, visibility, created_by, end_reason, is_18_plus, top_gaffer_enabled, requires_approval, is_multi_league, parent_competition_id, pontoon_match_days")
        .eq("id", competitionId)
        .single();

      if (!competitionData) return;

      // Multi-entry: load the current user's entries; the first is active by default.
      const { data: myEntryRows } = await supabase
        .from('user_selections')
        .select('id, entry_index, entry_label')
        .eq('user_id', user.id)
        .eq('competition_id', competitionId)
        .order('entry_index', { ascending: true });
      const myEntryList = myEntryRows || [];
      const resolvedActiveEntryId = myEntryList[0]?.id || null;
      setMyEntries(myEntryList);
      setActiveEntryId((prev) => prev || resolvedActiveEntryId);

      // Phase 2: load admin roster + this user's management role.
      const myRole = await loadCompAdmins(competitionData, user.id);

      // Phase 3: managers see a count of entries awaiting approval.
      if (myRole === 'owner' || myRole === 'admin' || userRole === 'admin') {
        const { count } = await supabase
          .from('user_selections')
          .select('id', { count: 'exact', head: true })
          .eq('competition_id', competitionId)
          .eq('status', 'pending');
        setPendingEntriesCount(count || 0);
      }

      // Run parallel queries for data that doesn't depend on each other
      const [
        creatorProfileResult,
        userSelectionResult,
        matchdaysResult,
        userRegistrationResult
      ] = await Promise.allSettled([
        // Fetch competition creator's username
        supabase
          .from("profiles")
          .select("username")
          .eq("id", competitionData.created_by)
          .single(),
        // Fetch the current user's team selection(s)
        supabase
          .from('team_selections')
          .select('selected_team, match_day, selection_number')
          .eq('user_id', user.id)
          .eq('competition_id', competitionId)
          .eq('match_day', competitionData.current_match_day)
          .order('selection_number', { ascending: true }),
        // Fetch matchdays for this competition
        supabase
          .from("matchdays_view")
          .select("match_day, first_kickoff_time, league, season")
          .eq("league", competitionData.league)
          .eq("season", competitionData.season),
        // Check if user is registered for this competition (>=1 entry).
        supabase
          .from("user_selections")
          .select("id")
          .eq("user_id", user.id)
          .eq("competition_id", competitionId)
          .limit(1)
          .maybeSingle()
      ]);

      // Handle creator username
      let createdByUsername = "Unknown";
      if (creatorProfileResult.status === 'fulfilled' && creatorProfileResult.value.data) {
        createdByUsername = creatorProfileResult.value.data.username || "Unknown";
      }

      setCompetition({ ...competitionData, created_by_username: createdByUsername });

      // Initialize matchDay to current match day
      setMatchDay(competitionData.current_match_day || 1);

      // Handle user selection(s)
      if (userSelectionResult.status === 'fulfilled' && userSelectionResult.value.data && userSelectionResult.value.data.length > 0) {
        // Join multiple selections with comma
        const selections = userSelectionResult.value.data.map(s => s.selected_team).join(', ');
        setMySelection(selections);
      } else {
        setMySelection(null);
      }

      // Handle matchdays
      if (matchdaysResult.status === 'fulfilled' && matchdaysResult.value.data) {
        setMatchdays(matchdaysResult.value.data);
      } else {
        setMatchdays([]);
      }

      // Handle user registration
      if (userRegistrationResult.status === 'fulfilled' && userRegistrationResult.value.data) {
        setIsRegistered(!!userRegistrationResult.value.data);
      } else {
        setIsRegistered(false);
      }

      // Fetch standings and registered players from the correct view
      let standingsData = [];
      let playersData = [];
      
      if (competitionData.type === "season") {
        const seasonStatsView =
          competitionData.status === "inactive"
            ? "user_competition_stats_all_view"
            : "user_competition_stats_view";

        // Fetch season data and team selections in parallel
        const [statsResult, selectionsResult] = await Promise.allSettled([
          supabase
            .from(seasonStatsView)
            .select("*")
            .eq("competition_id", competitionId)
            .order("rank", { ascending: true }),
          supabase
            .from('team_selections')
            .select('selected_team, user_id, entry_id')
            .eq('competition_id', competitionId)
            .eq('match_day', competitionData.current_match_day)
        ]);

        let seasonStatsRows = [];
        if (statsResult.status === 'fulfilled' && statsResult.value.data) {
          seasonStatsRows = statsResult.value.data;
        } else if (seasonStatsView !== "user_competition_stats_view") {
          const { data: fallbackRows } = await supabase
            .from("user_competition_stats_view")
            .select("*")
            .eq("competition_id", competitionId)
            .order("rank", { ascending: true });
          seasonStatsRows = fallbackRows || [];
        }

        if (seasonStatsRows.length > 0) {
          const data = seasonStatsRows;
          
          // Base mapping from the view
          standingsData = data.map((entry, idx) => ({
            ...entry,
            rank: entry.rank || idx + 1,
            username: entry.username || null,
            user_id: entry.user_id,
            wins: entry.wins,
            draws: entry.draws,
            losses: entry.losses,
            gf: entry.goals_for,
            ga: entry.goals_against,
            points: entry.points,
          }));

          playersData = data.map((entry) => ({
            user_id: entry.user_id,
            entry_id: entry.entry_id,
            entry_index: entry.entry_index,
            entry_label: entry.entry_label,
            username: entry.username || null,
            current_selection: entry.current_selection || "--",
            status: entry.selection_status || entry.status || "active",
          }));

          // Collect all user IDs that need profile lookups
          const missingProfileIds = [...new Set(data.filter(s => !s.username && s.user_id).map(s => s.user_id))];
          let selectionUserIds = [];
          let selMapByUser = {};
          let selMapByEntry = {};

          // Process team selections if available (keyed by entry for multi-entry)
          if (selectionsResult.status === 'fulfilled' && selectionsResult.value.data) {
            const selRows = selectionsResult.value.data;
            selRows.forEach(r => {
              if (r.user_id) selMapByUser[r.user_id] = r.selected_team;
              if (r.entry_id) selMapByEntry[r.entry_id] = selMapByEntry[r.entry_id]
                ? `${selMapByEntry[r.entry_id]}, ${r.selected_team}`
                : r.selected_team;
            });
            selectionUserIds = [...new Set(selRows.map(r => r.user_id).filter(Boolean))];
          }

          // Combine all user IDs that need profile lookups
          const allUserIds = [...new Set([...missingProfileIds, ...selectionUserIds])];

          // Fetch all needed profiles in one query
          let nameMap = {};
          let idToUsername = {};
          if (allUserIds.length > 0) {
            const { data: profiles } = await supabase
              .from('profiles')
              .select('id, username')
              .in('id', allUserIds);
            
            if (profiles) {
              profiles.forEach(p => { 
                nameMap[p.id] = p.username;
                idToUsername[p.id] = p.username;
              });
            }
          }

          // Update usernames in standings and players data (selection keyed by entry)
          standingsData = standingsData.map(s => ({
            ...s,
            username: s.username || nameMap[s.user_id] || null,
            current_selection: selMapByEntry[s.entry_id] || s.current_selection || '--'
          }));

          playersData = playersData.map(p => ({
            ...p,
            username: p.username || nameMap[p.user_id] || null,
            current_selection: selMapByEntry[p.entry_id] || p.current_selection || '--'
          }));
        }
      } else {
        // Fetch knockout data and team selections in parallel
        const [knockoutResult, selectionsResult] = await Promise.allSettled([
          supabase
            .from("knockout_leaderboard_view")
            .select("*")
            .eq("competition_id", competitionId),
          supabase
            .from('team_selections')
            .select('selected_team, user_id, entry_id')
            .eq('competition_id', competitionId)
            .eq('match_day', competitionData.current_match_day)
        ]);

        if (knockoutResult.status === 'fulfilled' && knockoutResult.value.data) {
          const data = knockoutResult.value.data;
          
          // Sort: active first, then by elimination round
          const sorted = data.sort((a, b) => {
            const isAActive = a.status === "Still In" || a.status === "active";
            const isBActive = b.status === "Still In" || b.status === "active";
            if (isAActive && !isBActive) return -1;
            if (!isAActive && isBActive) return 1;
            if (isAActive && isBActive) {
              return a.user_username.localeCompare(b.user_username);
            }
            return (b.knocked_out_on || 0) - (a.knocked_out_on || 0);
          });
          
          // Process team selections (keyed by entry for multi-entry)
          let selMapByEntry = {};
          if (selectionsResult.status === 'fulfilled' && selectionsResult.value.data) {
            const selRows = selectionsResult.value.data;
            selRows.forEach(r => {
              if (r.entry_id) selMapByEntry[r.entry_id] = selMapByEntry[r.entry_id]
                ? `${selMapByEntry[r.entry_id]}, ${r.selected_team}`
                : r.selected_team;
            });
          }

          standingsData = sorted.map((entry, idx) => ({
            ...entry,
            rank: idx + 1,
            username: entry.user_username,
            current_selection: selMapByEntry[entry.entry_id] || entry.current_selection || "--",
            elimination_matchday: entry.knocked_out_on,
          }));

          playersData = sorted.map((entry) => ({
            user_id: entry.user_id,
            entry_id: entry.entry_id,
            entry_index: entry.entry_index,
            entry_label: entry.entry_label,
            username: entry.user_username,
            current_selection: selMapByEntry[entry.entry_id] || entry.current_selection || "--",
            status: entry.status || "active",
          }));
        }
      }
      
      // Phase 3: pending/rejected entries are not approved members — keep them
      // out of the public standings and players list (they're managed on the
      // Manage Entries tab). Active + eliminated remain.
      const isApprovedMember = (r) => !['pending', 'rejected'].includes(r.status);
      standingsData = standingsData.filter(isApprovedMember);
      playersData = playersData.filter(isApprovedMember);

      setStandings(standingsData);
      setRegisteredPlayers(playersData);

      // Determine winner and fetch selection history in parallel
      const finalTasks = [];
      
      // Winner determination for inactive competitions
      if (competitionData.status === "inactive") {
        if (competitionData.type === "season") {
          // Season: top of standings is the winner
          setWinnerName(standingsData[0]?.username || null);
        } else {
          // Knockout: prefer stored winner id, else derive from standings/status
          if (competitionData.knockout_winner) {
            finalTasks.push(
              supabase
                .from("profiles")
                .select("username")
                .eq("id", competitionData.knockout_winner)
                .single()
            );
          } else {
            // Only show winner if no unfulfilled requirements
            const winnerEntry = standingsData.find((e) => {
              const isActive = ["Winner", "Still In", "active"].includes(e.status);
              if (!isActive) return false;
              
              // Check for unfulfilled requirements
              const hasAbandonment = e.abandoned_on_matchdays && Array.isArray(e.abandoned_on_matchdays) && e.abandoned_on_matchdays.length > 0;
              const hasUnfulfilledDoublePick = e.must_pick_double === true;
              
              // Only declare winner if NO unfulfilled requirements
              return !hasAbandonment && !hasUnfulfilledDoublePick;
            });
            setWinnerName(winnerEntry?.username || null);
          }
        }
      } else {
        setWinnerName(null);
      }

      // Fetch selection history if user is registered (for the active entry)
      if (user) {
        finalTasks.push(fetchSelectionHistory(competitionData, resolvedActiveEntryId));
      }

      // Execute final tasks in parallel
      if (finalTasks.length > 0) {
        const results = await Promise.allSettled(finalTasks);
        
        // Handle winner profile result if it was fetched
        if (competitionData.status === "inactive" && 
            competitionData.type !== "season" && 
            competitionData.knockout_winner && 
            results[0] && results[0].status === 'fulfilled') {
          setWinnerName(results[0].value.data?.username || null);
        }
      }

      setLoading(false);
    };
    fetchData();
  }, [competitionId, username]);

  // Fetch fixtures and extract available teams in one optimized query
  useEffect(() => {
    const fetchFixturesAndTeams = async () => {
      if (!competition) return;
      
      try {
        let fixtureData = [];
        let error = null;
        if (competition.is_multi_league) {
          // Show the fixtures actually used across the competition's rounds.
          const { data: mapRows } = await supabase
            .from("competition_round_matchdays")
            .select("league, season, match_day")
            .eq("competition_id", competition.id);
          const rows = mapRows || [];
          if (rows.length === 0) {
            setFixtures([]);
            setAvailableTeams([]);
            return;
          }
          const leagues = [...new Set(rows.map((r) => r.league))];
          const mds = [...new Set(rows.map((r) => r.match_day))];
          const pairSet = new Set(rows.map((r) => `${r.league}|${r.season}|${r.match_day}`));
          const res = await supabase
            .from("fixtures")
            .select("*")
            .in("league", leagues)
            .in("match_day", mds)
            .order("match_day", { ascending: true })
            .order("kickoff_time", { ascending: true });
          error = res.error;
          fixtureData = (res.data || []).filter((f) => pairSet.has(`${f.league}|${f.season}|${f.match_day}`));
        } else {
          const res = await supabase
            .from("fixtures")
            .select("*")
            .eq("league", competition.league)
            .eq("season", competition.season)
            .order("match_day", { ascending: true })
            .order("kickoff_time", { ascending: true });
          error = res.error;
          fixtureData = res.data;
        }

        if (error) {
          setFixtures([]);
          setAvailableTeams([]);
        } else {
          const fixtures = fixtureData || [];
          setFixtures(fixtures);
          
          // Extract unique teams from fixtures
          const teams = new Set();
          fixtures.forEach(fixture => {
            teams.add(fixture.home_team);
            teams.add(fixture.away_team);
          });
          const sortedTeams = Array.from(teams).sort();
          setAvailableTeams(sortedTeams);
          
          if (sortedTeams.length > 0 && !selectedTeam) {
            setSelectedTeam(sortedTeams[0]);
          }
        }
      } catch (error) {
        setFixtures([]);
        setAvailableTeams([]);
      }
    };
    
    fetchFixturesAndTeams();
  }, [competition, selectedTeam]);

  // Handle click outside for team dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (teamDropdownRef.current && !teamDropdownRef.current.contains(event.target)) {
        setTeamDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Listen for real-time fixture status change notifications
  useEffect(() => {
    if (!session?.user?.id) return;

    const unsubscribe = subscribeToChanges(supabase, 'user_notifications',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notification_log',
          filter: `user_id=eq.${session.user.id}`
        },
        (payload) => {
          const { title, message, notification_type } = payload.new;
          
          // Determine notification style based on type
          let notifyType = 'info';
          if (notification_type === 'fixture_status_change') {
            notifyType = 'warning';
          } else if (notification_type === 'competition_update') {
            notifyType = 'info';
          } else if (notification_type === 'system_announcement') {
            notifyType = 'info';
          }
          
          // Show toast notification with title (if available) or message
          notify(title || message, notifyType);
          
          // Refresh competition data to update banners and selection info
          setTimeout(() => {
            window.location.reload();
          }, 2000); // Give user 2 seconds to read the notification
        }
      );

    return () => {
      unsubscribe();
    };
  }, [session?.user?.id]);

  // For multi-league, the pager pages by ROUND: `matchDay` holds the round number
  // and a round's pool is the fixtures whose (league, match_day) map to that round.
  // For single-league it stays the fixtures matchday, as before.
  const mlRoundMap = competition?.is_multi_league ? (mlSummary?.rounds || []) : null;
  const totalPagerSteps = mlRoundMap
    ? Math.max(mlRoundMap.length, 1)
    : (competition?.total_match_days || 38);

  // Group fixtures by date, but only for the selected matchDay / round
  const fixturesByDate = fixtures
    .filter(f => {
      if (mlRoundMap) {
        const rmap = mlRoundMap[matchDay - 1];
        return rmap && rmap[f.league] === f.match_day;
      }
      return f.match_day === matchDay;
    })
    .reduce((acc, fixture) => {
      const date = fixture.kickoff_time
        ? new Date(fixture.kickoff_time).toLocaleDateString(undefined, {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })
        : "Unknown Date";
      if (!acc[date]) acc[date] = [];
      acc[date].push(fixture);
      return acc;
    }, {});

  const findFirstKickoff = (matchDay, league, season) => {
    const matchday = matchdays.find(
      (m) =>
        m.match_day === matchDay && m.league === league && m.season === season
    );
    return matchday ? new Date(matchday.first_kickoff_time) : null;
  };

  const ensureCompetitionAgeEligibility = async (userId, compObj) => {
    if (!compObj?.is_18_plus) {
      return { allowed: true };
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('date_of_birth')
      .eq('id', userId)
      .maybeSingle();

    if (!isAtLeastAge(profile?.date_of_birth, 18)) {
      return { allowed: false, message: 'This competition is 18+ only.' };
    }

    return { allowed: true };
  };

  // Registration logic for private competitions (modal submit)
  const handlePrivateJoin = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!pendingComp) return;
    const {
      compId,
      compObj
    } = pendingComp;
    // Validate the registration key against the stored competition key
    try {
      const { data: compData, error: compErr } = await supabase
        .from('competitions')
        .select('registration_key')
        .eq('id', compId)
        .single();
      if (compErr) {
        setKeyError('Failed to validate registration key. Please try again.');
        return;
      }
      const storedKey = compData?.registration_key || '';
      if (String(storedKey).trim() !== String(keyInput || '').trim()) {
        setKeyError('Invalid registration key. Please check and try again.');
        return;
      }
    } catch (err) {
      setKeyError('Failed to validate registration key. Please try again.');
      return;
    }
    const { data: { user } = {} } = await supabase.auth.getUser();
    if (!user) {
      setKeyError("You must be logged in.");
      return;
    }

    const ageCheck = await ensureCompetitionAgeEligibility(user.id, compObj);
    if (!ageCheck.allowed) {
      setKeyError(ageCheck.message);
      return;
    }

    // Insert into user_selections
    const { error: joinError } = await supabase
      .from("user_selections")
      .insert([{ user_id: user.id, competition_id: compId, league: compObj.league, season: compObj.season, match_day: compObj.start_match_day, status: compObj.requires_approval ? 'pending' : 'active' }]);
    if (joinError) {
      if (isAlreadyRegisteredError(joinError)) {
        setShowKeyModal(false);
        setKeyInput("");
        setKeyError("");
        setPendingComp(null);
        notify('You are already registered for this competition.', 'info');
        setIsRegistered(true);
        return;
      }
      setKeyError("Failed to join competition: " + joinError.message);
      return;
    }
    setRegisteredPlayers((prev) => [...prev, { username, current_selection: "--", status: "active" }]);
    setShowKeyModal(false);
    setKeyInput("");
    setKeyError("");
    setPendingComp(null);
  analytics.event('private_join_success', { competition_id: compId, competition_name: compObj.name });
  notify('Successfully joined competition!', 'success');
  setIsRegistered(true);
  };

  const isSelectionLocked = (matchDay, league, season) => {
    const kickoff = findFirstKickoff(matchDay, league, season);
    if (!kickoff) return false;

    const now = new Date();
    const lockTime = new Date(kickoff.getTime() - 60 * 60 * 1000);

    return now > lockTime;
  };

  // Register handler for the overview Register button
  const handleRegister = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      notify("You must be logged in to register.", 'error');
      return;
    }

    const ageCheck = await ensureCompetitionAgeEligibility(user.id, competition);
    if (!ageCheck.allowed) {
      notify(ageCheck.message, 'error');
      return;
    }

    // Check if already registered (>=1 entry). limit(1) keeps this safe under multi-entry.
    const { data: existing, error: existingError } = await supabase
      .from("user_selections")
      .select("id")
      .eq("user_id", user.id)
      .eq("competition_id", competition.id)
      .limit(1)
      .maybeSingle();

    if (existingError) {
      notify("Error checking registration: " + existingError.message, 'error');
      return;
    }
    if (existing) {
      notify("You are already registered for this competition.", 'info');
      return;
    }

    // If competition is private, open the key modal to validate
    if (competition.visibility === 'private') {
      setPendingComp({ compId: competition.id, compObj: competition });
      setShowKeyModal(true);
      analytics.event('private_join_attempt', { competition_id: competition.id, competition_name: competition.name });
      return;
    }

    // Insert registration (public or validated private)
    const { error: joinError } = await supabase
      .from("user_selections")
      .insert([
        {
          user_id: user.id,
          competition_id: competition.id,
          league: competition.league,
          season: competition.season,
          match_day: competition.start_match_day,
          status: competition.requires_approval ? 'pending' : 'active',
        },
      ]);
    if (joinError) {
      if (isAlreadyRegisteredError(joinError)) {
        notify("You are already registered for this competition.", 'info');
        setIsRegistered(true);
        return;
      }
      notify("Failed to register: " + joinError.message, 'error');
      return;
    }
    notify("Successfully registered!", 'success');
    setIsRegistered(true);
    // Optionally, refresh registered players
    setRegisteredPlayers((prev) => [
      ...prev,
      {
        username,
        current_selection: "--",
        status: "active",
      },
    ]);
  };

  const handleUnregister = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      notify("You must be logged in.", 'info');
      return;
    }
    const { error } = await supabase
      .from("user_selections")
      .delete()
      .eq("user_id", user.id)
      .eq("competition_id", competition.id);
    if (error) {
      notify("Failed to unregister: " + error.message, 'error');
      return;
    }
    notify("You have been unregistered.", 'success');
    setIsRegistered(false);
    setRegisteredPlayers((prev) =>
      prev.filter((p) => p.username !== username)
    );
  };

  // Multi-entry unregister: choose which entries to remove (all or a subset).
  const openUnregisterModal = () => {
    setUnregisterSelectedIds(myEntries.map((e) => e.id)); // default: all
    setShowUnregisterModal(true);
  };

  const toggleUnregisterEntry = (id) => {
    setUnregisterSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleUnregisterEntries = async () => {
    if (unregistering) return;
    if (unregisterSelectedIds.length === 0) {
      notify("Select at least one entry to remove.", 'info');
      return;
    }
    setUnregistering(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        notify("You must be logged in.", 'info');
        return;
      }
      // Deleting the entry rows cascades to their team_selections (FK ON DELETE CASCADE).
      const { error } = await supabase
        .from("user_selections")
        .delete()
        .eq("user_id", user.id)
        .in("id", unregisterSelectedIds);
      if (error) {
        notify("Failed to unregister: " + error.message, 'error');
        return;
      }
      const removedAll = unregisterSelectedIds.length >= myEntries.length;
      notify(
        removedAll
          ? "You have been unregistered."
          : `Removed ${unregisterSelectedIds.length} ${unregisterSelectedIds.length === 1 ? 'entry' : 'entries'}.`,
        'success'
      );
      setShowUnregisterModal(false);
      window.location.reload();
    } catch (err) {
      notify("Failed to unregister.", 'error');
    } finally {
      setUnregistering(false);
    }
  };

  const selectedStage = selectedStageId ? topGafferStages.find((s) => s.stage_id === selectedStageId) : null;
  const isUpcomingStage = selectedStage?.status === 'upcoming';
  const activeStandings = selectedStageId ? stageStandings : standings;

  // Top Gaffer management (creator/admin, before the competition starts).
  const tgIsCreatorOrAdmin = userRole === 'admin' || (competition && competition.created_by === session?.user?.id) || myCompRole === 'owner' || myCompRole === 'admin';
  const tgStartKickoff = tgAvailable.find((m) => m.match_day === competition?.start_match_day)?.first_kickoff_time;
  const tgStarted = tgStartKickoff ? new Date(tgStartKickoff) <= new Date() : false;
  const tgAvailableForStages = tgAvailable.filter((m) => m.match_day >= (competition?.start_match_day || 0));
  const tgStatusMeta = (status) =>
    status === 'complete'
      ? { label: 'Complete', cls: 'text-green-700 bg-green-100' }
      : status === 'in_progress'
      ? { label: 'In progress', cls: 'text-[#BA6830] bg-orange-100' }
      : { label: 'Upcoming', cls: 'text-gray-600 bg-gray-100' };

  function beginTgEdit() {
    const stages = [...topGafferStages]
      .sort((a, b) => a.stage_number - b.stage_number)
      .map((s) => s.matchdays || []);
    setTgEditValue({
      enabled: !!competition?.top_gaffer_enabled,
      stageCount: stages.length || 1,
      stages,
    });
    setTgManaging(true);
  }

  async function saveTgConfig() {
    const val = tgEditValue;
    if (val.enabled) {
      const available = tgAvailableForStages.map((m) => m.match_day);
      const stages = val.stages || [];
      const allocated = new Set(stages.flat());
      if (stages.length < 1 || stages.some((s) => !s || s.length === 0)) {
        notify('Each Top Gaffer stage must contain at least one matchday.', 'info');
        return;
      }
      if (allocated.size !== available.length || available.some((md) => !allocated.has(md))) {
        notify('Assign every matchday to a stage before saving.', 'info');
        return;
      }
    }
    setTgSaving(true);
    const p_stages = (val.stages || []).map((mds, i) => ({ stage_number: i + 1, matchdays: mds }));
    const { error } = await supabase.rpc('save_top_gaffer_config', {
      p_competition_id: competitionId,
      p_enabled: !!val.enabled,
      p_stages: val.enabled ? p_stages : [],
    });
    setTgSaving(false);
    if (error) {
      notify(`Top Gaffer save failed: ${error.message}`, 'error');
      return;
    }
    notify('Top Gaffer settings saved.', 'success');
    setTgManaging(false);
    const { data: stagesData } = await supabase.rpc('get_top_gaffer_stages', { p_competition_id: competitionId });
    setTopGafferStages(stagesData || []);
    setCompetition((c) => (c ? { ...c, top_gaffer_enabled: !!val.enabled } : c));
  }
  const totalStandingsPages = Math.max(1, Math.ceil((activeStandings || []).length / STANDINGS_PER_PAGE));
  const paginatedStandings = (activeStandings || []).slice((standingsPage - 1) * STANDINGS_PER_PAGE, standingsPage * STANDINGS_PER_PAGE);
  const setStandingsPageSafe = (p) => {
    const page = Math.max(1, Math.min(totalStandingsPages, p));
    setStandingsPage(page);
  };

  const totalRegisteredPages = Math.max(1, Math.ceil((registeredPlayers || []).length / REG_PLAYERS_PER_PAGE));
  const paginatedRegisteredPlayers = (registeredPlayers || []).slice((regPlayersPage - 1) * REG_PLAYERS_PER_PAGE, regPlayersPage * REG_PLAYERS_PER_PAGE);
  const setRegPageSafe = (p) => {
    const page = Math.max(1, Math.min(totalRegisteredPages, p));
    setRegPlayersPage(page);
  };

  // Update available teams when the managed entry changes (filter by eligible teams)
  useEffect(() => {
    const fetchEligibleTeams = async () => {
      if (!manageEntryId || !competition) return;

      try {
        const { data, error } = await supabase
          .rpc('get_eligible_teams_for_entry', {
            p_entry_id: manageEntryId
          });

        if (error) {
          // Fall back to all available teams
        } else if (data && data.length > 0) {
          const eligibleTeamNames = data.map(t => t.team);
          setAvailableTeams(eligibleTeamNames);
          setManageTeam(eligibleTeamNames[0] || '');
        }
      } catch (err) {
      }
    };

    fetchEligibleTeams();
  }, [manageEntryId, competition]);

  useEffect(() => {
    if (registeredPlayers.length > 0) {
      setManageEntryId(registeredPlayers[0].entry_id);
    }
  }, [registeredPlayers]);

  // Load the entry-management roster when that tab opens.
  useEffect(() => {
    if (activeTab === 'manage-entries') {
      loadManageEntries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, competitionId]);

  // Load the multi-league round editor when that tab opens.
  useEffect(() => {
    if (activeTab === 'match-days' && competition?.is_multi_league) {
      loadRoundEditor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, competitionId, competition?.is_multi_league]);

  // Load the multi-league summary for the Overview (all viewers).
  useEffect(() => {
    if (!competition?.is_multi_league) { setMlSummary(null); return; }
    let cancelled = false;
    (async () => {
      const { data: rows } = await supabase
        .from('competition_round_matchdays')
        .select('round_number, league, season, match_day')
        .eq('competition_id', competitionId)
        .order('round_number', { ascending: true });
      if (cancelled) return;
      const list = rows || [];
      const leagues = [...new Set(list.map((r) => r.league))];
      const season = list[0]?.season || competition?.season || '';
      const maxRound = list.reduce((m, r) => Math.max(m, r.round_number), 0);
      const rounds = [];
      for (let i = 0; i < maxRound; i++) rounds.push({});
      list.forEach((r) => { rounds[r.round_number - 1][r.league] = r.match_day; });
      setMlSummary({ leagues, season, rounds });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionId, competition?.is_multi_league]);

  // Load this Knockout's linked Pontoon (if any), for the Overview banner.
  useEffect(() => {
    if (!PONTOON_ENABLED || competition?.type !== 'knockout') { setLinkedPontoon(null); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('competitions')
        .select('id, name')
        .eq('parent_competition_id', competitionId)
        .eq('type', 'pontoon')
        .order('created_at', { ascending: true })
        .limit(1);
      if (!cancelled) setLinkedPontoon(data && data[0] ? data[0] : null);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionId, competition?.type]);

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-canvas">
        {/* Responsive green banner for mobile */}
        <div className="block md:hidden w-full bg-ui-green py-3 px-4 sticky top-0 z-40">
          <span className="text-white text-lg font-semibold">Footy Coach</span>
        </div>
        <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
        <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
        <div role="main" id="fc-main-content" className="md:ml-64 pt-8 px-2 sm:px-4 md:p-8 pb-24 md:pb-0 fc-workspace">
          <LoadingState label="Loading competition..." />
        </div>
      </div>
    );
  }

  if (!competition) {
    return (
      <div className="min-h-screen bg-surface-canvas">
        {/* Responsive green banner for mobile */}
        <div className="block md:hidden w-full bg-ui-green py-3 px-4 sticky top-0 z-40">
          <span className="text-white text-lg font-semibold">Footy Coach</span>
        </div>
        <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
        <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
        <div role="main" id="fc-main-content" className="md:ml-64 pt-8 px-2 sm:px-4 md:p-8 pb-24 md:pb-0 fc-workspace">
          <p className="p-6 text-brand-text">Competition not found.</p>
        </div>
      </div>
    );
  }

  // Pontoon competitions get a dedicated view (submissions table), not the
  // generic Knockout/Season tabs.
  if (competition.type === 'pontoon') {
    return <PontoonView competition={competition} session={session} userRole={userRole} />;
  }

  // Tabbed navigation


  // Helper: render status label
  const statusLabel = (status) => {
    if (status === "Still In" || status === "active") return "Active";
    // Only show Eliminated for knockout competitions
    if (competition?.type === "knockout") {
      return "Eliminated";
    }
    return "Active"; // Season/Scout competitions are always active
  };

  // Helper: render elimination matchday
  const eliminationMatchday = (entry) => {
    if (entry.status === "Still In" || entry.status === "active") return "--";
    return entry.elimination_matchday ? `Match Day ${entry.elimination_matchday}` : "--";
  };

  function ordinalSuffix(n) {
    if (!n) return '';
    const s = ["th", "st", "nd", "rd"], v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  }

  // Returns true when the selection window for the competition's current matchday is closed
  const isSelectionWindowClosed = () => {
    if (!competition) return false;
    return isSelectionLocked(competition.current_match_day, competition.league, competition.season);
  };

  const renderSelection = (entry) => {
    // Only show "Eliminated" status for knockout competitions
    if (entry.status === "Eliminated" && competition?.type === "knockout") {
        return "Eliminated";
    }

    const sel = entry.current_selection;
    if (!sel) {
        return "--";
    }
    // Always show the logged-in user's selection, even if window is not closed
    if (username && entry.username === username) {
        return sel;
    }
    // If selection exists but window not closed, show Hidden in grey italics
    if (!isSelectionWindowClosed()) {
        return <span className="italic text-content-secondary">Hidden</span>;
    }

    return sel;
  };

  // Multi-entry display helpers -------------------------------------------------
  // Suffix the entry number when a user holds more than one entry in this comp.
  const entryCountByUser = {};
  (registeredPlayers || []).forEach((p) => {
    if (p.user_id) entryCountByUser[p.user_id] = (entryCountByUser[p.user_id] || 0) + 1;
  });
  const displayName = (entry) => {
    const base = entry.username || entry.user_username || 'Player';
    if (entry.entry_label) return entry.entry_label;
    const isMulti = (entryCountByUser[entry.user_id] || 0) > 1 || (entry.entry_index && entry.entry_index > 1);
    return isMulti && entry.entry_index ? `${base} ${entry.entry_index}` : base;
  };
  // Label for one of the current user's own entries (dropdowns).
  const myEntryName = (e) => e.entry_label || `${username || 'Entry'} ${e.entry_index}`;

  // Switch the active entry (Overview / My Selections) and reload its data.
  const handleActiveEntryChange = async (newEntryId) => {
    if (!newEntryId || newEntryId === activeEntryId) return;
    setActiveEntryId(newEntryId);
    await fetchSelectionHistory(competition, newEntryId);
  };

  // Create another entry in this competition (from the competition view).
  const handleAddEntry = async () => {
    if (addingEntry || !competition) return;
    setAddingEntry(true);
    try {
      const { data, error } = await supabase.rpc('add_competition_entry', {
        p_competition_id: competition.id,
        p_entry_label: null,
      });
      if (error) {
        notify(error.message || 'Failed to add entry.', 'error');
        return;
      }
      notify(`New entry created (entry ${data?.entry_index || ''}).`, 'success');
      window.location.reload();
    } catch (err) {
      notify('Something went wrong adding an entry.', 'error');
    } finally {
      setAddingEntry(false);
    }
  };

  // Determine current user's ACTIVE entry (multi-entry aware), else fall back.
  const currentUserEntry = (
    (activeEntryId && standings.find((s) => s.entry_id === activeEntryId)) ||
    (activeEntryId && registeredPlayers.find((p) => p.entry_id === activeEntryId)) ||
    standings.find((s) => s.username === username) ||
    registeredPlayers.find((p) => p.username === username) ||
    null
  );

  // Counts: unique players vs total entries.
  const activeEntriesList = competition?.type === "knockout"
    ? (registeredPlayers || []).filter((player) => ["Still In", "active", "Winner"].includes(player.status))
    : (registeredPlayers || []);
  const activeEntries = activeEntriesList.length;
  const activePlayers = new Set(activeEntriesList.map((p) => p.user_id)).size;
  const totalRegisteredPlayers = (registeredPlayers || []).length;

  // Phase 2: management permissions. Owner ⊃ Admin ⊃ Entrant; global app admins
  // (profiles.role='admin') act as owner-level.
  const isGlobalAdmin = userRole === 'admin';
  const canManage = isGlobalAdmin || myCompRole === 'owner' || myCompRole === 'admin';
  const canManageAdmins = isGlobalAdmin || myCompRole === 'owner';
  const adminRoster = compAdmins.filter((a) => a.role === 'admin');
  // Live count of entries awaiting approval (reflects Manage Entries actions once
  // that tab has loaded; otherwise the count fetched on page load).
  const livePendingCount = manageEntries.length > 0
    ? manageEntries.filter((e) => e.status === 'pending').length
    : pendingEntriesCount;

  // Entrants who can be made admins: unique entered users, excluding the owner
  // and anyone already a manager.
  const existingManagerIds = new Set(
    [competition?.created_by, ...compAdmins.map((a) => a.user_id)].filter(Boolean)
  );
  const adminCandidates = (() => {
    const seen = new Map();
    (registeredPlayers || []).forEach((p) => {
      if (p.user_id && !existingManagerIds.has(p.user_id) && !seen.has(p.user_id)) {
        seen.set(p.user_id, p.username || p.user_username || 'Unknown');
      }
    });
    return Array.from(seen, ([user_id, username]) => ({ user_id, username }));
  })();

  // Add an administrator chosen from the competition's entrants (owner only; RLS re-checks).
  const handleAddAdmin = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setAdminError("");
    const userId = newAdminUserId;
    if (!userId) return;
    const candidate = adminCandidates.find((c) => c.user_id === userId);
    setAdminActionBusy(true);
    try {
      const { error } = await supabase.from('competition_admins').insert({
        competition_id: competition.id,
        user_id: userId,
        role: 'admin',
        added_by: session?.user?.id,
      });
      if (error) {
        setAdminError(error.message || 'Failed to add administrator.');
        return;
      }
      setNewAdminUserId("");
      notify(`${candidate?.username || 'User'} added as an administrator.`, 'success');
      await loadCompAdmins(competition, session?.user?.id);
    } catch (err) {
      setAdminError('Something went wrong adding the administrator.');
    } finally {
      setAdminActionBusy(false);
    }
  };

  const handleRemoveAdmin = async (userId) => {
    if (userId === session?.user?.id) {
      setAdminError("You can't remove yourself as an administrator.");
      return;
    }
    setAdminActionBusy(true);
    setAdminError("");
    try {
      const { error } = await supabase
        .from('competition_admins')
        .delete()
        .eq('competition_id', competition.id)
        .eq('user_id', userId)
        .eq('role', 'admin');
      if (error) {
        setAdminError(error.message || 'Failed to remove administrator.');
        return;
      }
      notify('Administrator removed.', 'success');
      await loadCompAdmins(competition, session?.user?.id);
    } catch (err) {
      setAdminError('Something went wrong removing the administrator.');
    } finally {
      setAdminActionBusy(false);
    }
  };

  // Render actions for Overview right column (register/select buttons & status badges)
  const renderOverviewActions = () => {
    if (competition?.type === 'world_stage') {
      return (
        <div className="flex flex-col items-end space-y-3">
          <div className="flex flex-col items-end space-y-2">
            {competition.registration_status === "open" ? (
              isRegistered ? (
                <FcButton variant="tertiary" type="submit"
                  className=""
                  onClick={handleUnregister}
                >
                  Unregister
                </FcButton>
              ) : (
                <FcButton variant="primary" type="submit"
                  className=""
                  onClick={handleRegister}
                >
                  Register
                </FcButton>
              )
            ) : null}

            {isRegistered && (
              <FcLinkButton variant="primary"
                to={`/world-stage/${competitionId}`}
                className="whitespace-nowrap inline-block"
              >
                Open World Stage
              </FcLinkButton>
            )}
          </div>
        </div>
      );
    }

    const hasSelection = Boolean(mySelection) || Boolean(currentUserEntry && currentUserEntry.current_selection && currentUserEntry.current_selection !== "--");
    return (
      <div className="flex flex-col items-end space-y-3">
        <div className="flex flex-col items-end space-y-2">
          {competition.registration_status === "open" ? (
            isRegistered ? (
              <FcButton variant="tertiary" type="submit"
                className=""
                onClick={openUnregisterModal}
              >
                Unregister
              </FcButton>
            ) : (
              <FcButton variant="primary" type="submit"
                className=""
                onClick={handleRegister}
              >
                Register
              </FcButton>
            )
          ) : null}

          {isRegistered && competition.status === 'active' ? (
            currentUserEntry ? (
              currentUserEntry.status !== "Still In" && currentUserEntry.status !== "active" && competition?.type === "knockout" ? (
                <span className="px-3 py-1 rounded text-xs font-semibold text-white bg-red-600">Eliminated</span>
              ) : isSelectionWindowClosed() ? (
                // If mustPickDouble in knockout, user can bypass lock (no chip needed)
                (mustPickDouble && competition?.type === 'knockout') ? (
                  <FcLinkButton variant="primary" to={`/select-team/${competitionId}${activeEntryId ? `?entry=${activeEntryId}` : ""}`} className="whitespace-nowrap inline-block">
                    {hasSelection ? "Edit Team" : "Select Team"}
                  </FcLinkButton>
                ) : chipsRemaining > 0 && !hasSelection ? (
                  <FcLinkButton variant="primary" to={`/select-team/${competitionId}${activeEntryId ? `?entry=${activeEntryId}` : ""}`} className="whitespace-nowrap inline-block">
                    Use Last Chance Chip ({chipsRemaining})
                  </FcLinkButton>
                ) : (
                  <span className="px-3 py-1 rounded text-xs font-semibold text-content-primary bg-gray-200">Selection Closed</span>
                )
              ) : (
                <FcLinkButton variant="primary" to={`/select-team/${competitionId}${activeEntryId ? `?entry=${activeEntryId}` : ""}`} className="whitespace-nowrap inline-block">
                  {hasSelection ? "Edit Team" : "Select Team"}
                </FcLinkButton>
              )
            ) : (
              <FcLinkButton variant="primary" to={`/select-team/${competitionId}${activeEntryId ? `?entry=${activeEntryId}` : ""}`} className="">Select Team</FcLinkButton>
            )
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-surface-canvas">
      {/* Responsive green banner for mobile */}
      <div className="block md:hidden w-full bg-ui-green py-3 px-4 sticky top-0 z-40">
        <span className="text-white text-lg font-semibold">Footy Coach</span>
      </div>
      <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
      <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
      <div role="main" id="fc-main-content" className="md:ml-64 pt-8 px-2 sm:px-4 md:p-8 max-w-4xl mx-auto pb-24 md:pb-0 fc-workspace">
        <div className="fc-competition-heading"><h1 className="text-brand-text">
          {competition.name}
        </h1><CompetitionCardHeader comp={{...competition, league_logo_url:leagueMedia?.league===competition.league?leagueMedia?.logo:null}} leagueBranding compact presentation="context"/></div>
        {competition.type === 'knockout' && isRegistered && (
          <div className="mb-4"><LastChanceChips count={chipBalance} compact /></div>
        )}
  {competition.status === "inactive" && competition.end_reason === "all_eliminated" && (
          <div className="mb-6 p-4 rounded-lg bg-gradient-to-r from-red-200 via-red-100 to-red-200 border border-red-300 shadow">
            <div className="text-xl font-extrabold text-ui-error">❌ All Players Eliminated</div>
            <div className="text-sm text-ui-error mt-1">No winner for {competition.name} - all participants were eliminated.</div>
          </div>
        )}
        {competition.status === "inactive" && winnerName && competition.end_reason !== "all_eliminated" && (
          <div className="mb-6 p-4 rounded-lg bg-gradient-to-r from-yellow-200 via-yellow-100 to-yellow-200 border border-yellow-300 shadow">
            <div className="text-xl font-extrabold text-yellow-800">🏆 Champion: {winnerName}</div>
            <div className="text-sm text-yellow-700 mt-1">🎉 Congratulations on winning {competition.name}! 🎊</div>
          </div>
        )}
        <div>
            <FcButton variant="tertiary" type="submit" onClick={() => setShowHowModal(true)} className="hover:underline">How to play</FcButton>
          </div><FcTabs label="Competition sections" value={activeTab} onValueChange={setActiveTab} items={[{id:"overview",label:<>Overview</>},{id:"standings",label:<>Standings</>},{id:"registered-players",label:<>Players</>},{id:"fixtures",label:<>Fixtures</>},...((isRegistered) ? [{id:"my-selections",label:<>My Selections</>}] : []),...((canManage && competition?.is_multi_league) ? [{id:"match-days",label:<>
                Match Days
              </>}] : []),...((canManage) ? [{id:"manage-entries",label:<>
                Manage Entries
              </>}] : []),...((canManage) ? [{id:"manage-selections",label:<>
                Manage Selections
              </>}] : []),...((canManageAdmins) ? [{id:"admins",label:<>
                Admins
              </>}] : [])]}>

        {/* Tab content */}
        {activeTab === "overview" && (
          <div className="space-y-6 mb-6">
            {/* Manager alert: entries awaiting approval */}
            {canManage && livePendingCount > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">⏳</span>
                  <div>
                    <div className="font-semibold text-amber-800">
                      {livePendingCount} {livePendingCount === 1 ? 'entry is' : 'entries are'} awaiting your approval
                    </div>
                    <div className="text-sm text-amber-700">Review them to let these players into the competition.</div>
                  </div>
                </div>
                <FcButton variant="primary" aria-pressed={activeTab === 'manage-entries'}
                  type="button"
                  onClick={() => setActiveTab('manage-entries')}
                  className="self-start sm:self-auto whitespace-nowrap"
                >
                  Review entries
                </FcButton>
              </div>
            )}

            {/* Linked Pontoon side-game banner */}
            {linkedPontoon && (
              <div className="bg-ui-green/5 border border-[#284239]/20 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🃏</span>
                  <div>
                    <div className="font-semibold text-ui-green">This Knockout has a Pontoon side-game</div>
                    <div className="text-sm text-content-secondary">
                      Players are auto-enrolled when they&apos;re knocked out. View submissions, scores and weekly winners.
                    </div>
                  </div>
                </div>
                <FcLinkButton variant="primary"
                  to={`/competition/${linkedPontoon.id}`}
                  className="self-start sm:self-auto whitespace-nowrap"
                >
                  View Pontoon →
                </FcLinkButton>
              </div>
            )}

            <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow p-4">
              <h3 className="text-xl font-bold mb-2">Overview</h3>

              {/* New two-column layout: details on the left, actions & current selection on the right */}
              <div className="fc-detail-actions">
                <div className="min-w-0">
                  <div className="mb-2"><strong>Created By:</strong> {competition.created_by_username || "Unknown"}</div>

                  {/* Match Day Display - show final matchday for inactive competitions */}
                  {competition.status === 'inactive' ? (
                    <div className="mb-2">
                      <strong>Final Match Day:</strong> {competition.current_match_day}
                    </div>
                  ) : (
                    <div className="mb-2">
                      <strong>Current Match Day:</strong> {competition.current_match_day}
                    </div>
                  )}

                  <div className="mb-2"><strong>Status:</strong> {competition.status}</div>
                  <div className="mb-2"><strong>Active Players:</strong> {activePlayers}</div>
                  <div className="mb-2"><strong>Active Entries:</strong> {activeEntries}</div>

                  {/* Multi-entry: switch between your entries / add another */}
                  {isRegistered && (competition.type === 'season' || competition.type === 'knockout') && myEntries.length > 0 && (
                    <EntrySelector className="mb-2" aria-label="Your entry"
                          value={activeEntryId || ''}
                          onChange={(e) => handleActiveEntryChange(e.target.value)}
                          readOnlyValue={myEntries.length>1?undefined:(myEntries[0]?myEntryName(myEntries[0]):'--')}
                          action={competition.status === 'active' && competition.registration_status === 'open' && (
                        <FcButton variant="secondary" size="compact"
                          type="button"
                          onClick={handleAddEntry}
                          disabled={addingEntry}
                          className="border disabled:cursor-not-allowed"
                        >
                          {addingEntry ? 'Adding…' : '+ Add entry'}
                        </FcButton>
                      )}>
                        {myEntries.map(e=><option key={e.id} value={e.id}>{myEntryName(e)}</option>)}
                    </EntrySelector>
                  )}

                  {/* Current Selection or End Status */}
                  {competition.status === 'inactive' && currentUserEntry ? (
                    <div className="mt-2">
                      {currentUserEntry.status === 'eliminated' ? (
                        <div>
                          <strong>Result:</strong> <span className="font-semibold text-ui-error ml-2">Eliminated on Match Day {currentUserEntry.knocked_out_on || competition.current_match_day}</span>
                        </div>
                      ) : competition.end_reason === 'knockout_winner' && (competition.knockout_winner_entry_id ? competition.knockout_winner_entry_id === currentUserEntry.entry_id : competition.knockout_winner === currentUserEntry.user_id) ? (
                        <div>
                          <strong>Result:</strong> <span className="font-semibold text-yellow-600 ml-2">🏆 Champion!</span>
                        </div>
                      ) : competition.end_reason === 'season_complete' ? (
                        <div>
                          <strong>Result:</strong> <span className="font-semibold text-ui-green ml-2">Season Complete - View Final Standings</span>
                        </div>
                      ) : (
                        <div>
                          <strong>Result:</strong> <span className="font-semibold text-content-secondary ml-2">Competition Ended</span>
                        </div>
                      )}
                    </div>
                  ) : currentUserEntry ? (
                    <div className="mt-2">
                      <strong>Current Selection:</strong> <span className="font-semibold text-action-primary ml-2">{mySelection || renderSelection(currentUserEntry)}</span>
                    </div>
                  ) : null}

                  {/* Postponement Credits Banner (Season) */}
                  {isRegistered && competition.type === 'season' && postponementCredits > 0 && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">🎫</span>
                        <div>
                          <div className="font-semibold text-yellow-800">Postponement Credit Available</div>
                          <div className="text-sm text-yellow-700">
                            You can select 2 teams in an upcoming matchday. Credits are non-stackable.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Double-Pick Requirement Banner (Knockout) */}
                  {isRegistered && competition.type === 'knockout' && mustPickDouble && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-300 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">⚠️</span>
                        <div>
                          <div className="font-semibold text-ui-error">Double-Pick Required</div>
                          <div className="text-sm text-ui-error">
                            Your fixture was abandoned/postponed. You must pick 2 teams this matchday - BOTH must win to survive!
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {renderOverviewActions()}
              </div>
            </FcCard>

            <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow p-4">
              <h3 className="text-xl font-bold mb-2">Details</h3>
              {competition.is_multi_league ? (
                <>
                  <p>
                    <strong>Format:</strong>{" "}
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-action-primary text-white align-middle">
                      Multi League
                    </span>
                  </p>
                  <p>
                    <strong>Leagues:</strong>{" "}
                    {mlSummary?.leagues?.length
                      ? mlSummary.leagues.join(", ")
                      : "Loading..."}
                  </p>
                  <p><strong>Season:</strong> {mlSummary?.season || competition.season}</p>
                  <p><strong>Type:</strong> {competition.type}</p>
                  <p>
                    <strong>Rounds:</strong>{" "}
                    {mlSummary?.rounds?.length ?? 0}
                  </p>
                  <p><strong>Status:</strong> {competition.status}</p>
                  <p><strong>Registered Players:</strong> {totalRegisteredPlayers}</p>

                  {mlSummary?.rounds?.length > 0 && (
                    <div className="mt-3 border-t pt-3">
                      <div className="text-sm font-semibold text-brand-text mb-1">Round match days</div>
                      <div className="overflow-x-auto">
                        <FcTable label="Competition View table 1" className="text-sm w-full">
                          <thead>
                            <tr className="text-left text-brand-text/70">
                              <th scope="col" className="pr-4 py-1 font-medium">Round</th>
                              {mlSummary.leagues.map((lg) => (
                                <th scope="col" key={lg} className="pr-4 py-1 font-medium">{lg}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {mlSummary.rounds.map((rmap, idx) => {
                              const isCurrent = (competition.current_match_day || 1) === idx + 1;
                              return (
                                <tr key={idx} className={isCurrent ? "font-semibold text-ui-green" : ""}>
                                  <td className="pr-4 py-1">
                                    {idx + 1}{isCurrent ? " (current)" : ""}
                                  </td>
                                  {mlSummary.leagues.map((lg) => (
                                    <td key={lg} className="pr-4 py-1">
                                      {rmap && rmap[lg] != null ? `MD ${rmap[lg]}` : "—"}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </FcTable>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <p><strong>League:</strong> {competition.league}</p>
                  <p><strong>Season:</strong> {competition.season}</p>
                  <p><strong>Type:</strong> {competition.type}</p>
                  <p><strong>Status:</strong> {competition.status}</p>
                  <p><strong>Registered Players:</strong> {totalRegisteredPlayers}</p>
                </>
              )}
            </FcCard>

            {competition.type === 'season' && (competition.top_gaffer_enabled || tgIsCreatorOrAdmin) && (
              <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-bold">Top Gaffer</h3>
                  {tgIsCreatorOrAdmin && !tgStarted && !tgManaging && (
                    <FcButton variant="primary" type="submit"
                      onClick={beginTgEdit}
                      className=""
                    >
                      {competition.top_gaffer_enabled ? 'Edit stages' : 'Set up Top Gaffer'}
                    </FcButton>
                  )}
                </div>

                {!tgManaging && competition.top_gaffer_enabled && topGafferStages.length > 0 && (
                  <div>
                    <p className="text-sm text-content-primary mb-3">
                      {topGafferStages.length} stage {topGafferStages.length === 1 ? 'award' : 'awards'} throughout the season.
                    </p>
                    <ul className="divide-y divide-gray-100">
                      {topGafferStages.map((s) => {
                        const meta = tgStatusMeta(s.status);
                        return (
                          <li key={s.stage_id} className="flex items-center justify-between gap-2 py-1.5 text-sm">
                            <span className="text-ui-green font-medium">Stage {s.stage_number}</span>
                            <span className="text-content-secondary">MD{s.first_matchday}–MD{s.last_matchday}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${meta.cls}`}>{meta.label}</span>
                          </li>
                        );
                      })}
                    </ul>
                    <p className="text-xs text-content-secondary mt-2">
                      Switch the Standings tab to any stage to see its leaderboard.
                    </p>
                  </div>
                )}

                {!tgManaging && !competition.top_gaffer_enabled && tgIsCreatorOrAdmin && !tgStarted && (
                  <p className="text-sm text-content-secondary">Top Gaffer isn&apos;t set up for this competition yet.</p>
                )}

                {tgIsCreatorOrAdmin && tgStarted && (
                  <p className="text-xs text-content-secondary mt-2">
                    Top Gaffer settings cannot be changed after the competition has started.
                  </p>
                )}

                {tgManaging && tgIsCreatorOrAdmin && !tgStarted && (
                  <div>
                    <TopGafferConfig
                      availableMatchdays={tgAvailableForStages}
                      value={tgEditValue}
                      onChange={setTgEditValue}
                    />
                    <div className="flex gap-2 mt-2">
                      <FcButton variant="tertiary" type="submit"
                        onClick={saveTgConfig}
                        disabled={tgSaving}
                        className=""
                      >
                        {tgSaving ? 'Saving…' : 'Save Top Gaffer'}
                      </FcButton>
                      <FcButton variant="tertiary" type="submit"
                        onClick={() => setTgManaging(false)}
                        className=""
                      >
                        Cancel
                      </FcButton>
                    </div>
                  </div>
                )}
              </FcCard>
            )}
          </div>
        )}
        {activeTab === "fixtures" && (
          <div className="mb-8">
            {/* Restore original fixtures section UI and logic */}
            <div className="flex flex-col gap-3 mb-4">
              <div className="font-bold text-lg text-brand-text">Fixtures</div>
              {/* View mode toggle and team dropdown */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <SegmentedControl label="Fixture view" value={fixturesViewMode} onValueChange={setFixturesViewMode} options={[{value:'matchday',label:'Matchday'},{value:'team',label:'Team'}]}/>
                {/* Team dropdown - moves to new line on mobile */}
                {fixturesViewMode === "team" && (
                  <div className="relative w-full sm:w-auto max-w-xs" ref={teamDropdownRef}>
                    <FcButton variant="secondary" type="submit" aria-expanded={teamDropdownOpen} aria-label={`Filter fixtures by team: ${selectedTeam}`}
                      onClick={() => setTeamDropdownOpen(!teamDropdownOpen)}
                      className="border cursor-pointer flex items-center justify-between w-full"
                    >
                      <span>{selectedTeam}</span>
                      <svg
                        className={`w-4 h-4 transition-transform ${teamDropdownOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </FcButton>

                    {/* Dropdown Options */}
                    {teamDropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 w-full bg-surface-card border border-ui-border rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                        {availableTeams.map((team) => (
                          <FcButton aria-pressed={selectedTeam === team} variant="tertiary" type="submit"
                            key={team}
                            onClick={() => {
                              setSelectedTeam(team);
                              setTeamDropdownOpen(false);
                            }}
                            className={`w-full text-left ${
                              selectedTeam === team
                                ? ""
                                : ""
                            }`}
                          >
                            {team}
                          </FcButton>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Conditional header based on view mode */}
            {fixturesViewMode === "matchday" ? (
              <div className="bg-ui-green text-white font-bold text-lg p-2 rounded-t-lg flex items-center justify-between">
                <FcButton variant="tertiary" type="submit"
                    className=""
                    onClick={() => setMatchDay((prev) => Math.max(prev - 1, 1))}
                    disabled={matchDay <= 1}
                >
                    &lt;
                </FcButton>
                <span>{competition.is_multi_league ? `Round ${matchDay}` : `Matchday ${matchDay}`}</span>
                <FcButton variant="tertiary" type="submit"
                    className=""
                    onClick={() => setMatchDay((prev) => Math.max(Math.min(prev + 1, totalPagerSteps), 1))}
                    disabled={matchDay >= totalPagerSteps}
                >
                    &gt;
                </FcButton>
              </div>
            ) : (
              <div className="bg-ui-green text-white font-bold text-lg p-2 rounded-t-lg text-center">
                {selectedTeam} Fixtures
              </div>
            )}

            {/* Fixtures display */}
            {Object.keys(fixturesByDate).length === 0 ? (
                <div className="w-full bg-gray-100 text-content-secondary rounded-lg p-6 text-center text-base font-medium">
                    {fixturesViewMode === "matchday"
                      ? (competition.is_multi_league
                          ? "No fixtures mapped for this round."
                          : "No fixtures available for this match day.")
                      : `No fixtures available for ${selectedTeam}.`}
                </div>
            ) : fixturesViewMode === "team" ? (
                // Team view: chronological list with date/time on left
                <FcCard as="div" padding="none" className="bg-surface-card rounded-b-lg shadow border border-ui-border">
                  {(() => {
                    // Filter fixtures by selected team first, then group by date for team view
                    const teamFixtures = fixtures.filter(fixture =>
                      fixture.home_team === selectedTeam || fixture.away_team === selectedTeam
                    );

                    const fixturesByDateTeam = teamFixtures.reduce((acc, fixture) => {
                      const date = fixture.kickoff_time
                        ? new Date(fixture.kickoff_time).toLocaleDateString(undefined, {
                            weekday: "long",
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })
                        : "TBD";
                      if (!acc[date]) acc[date] = [];
                      acc[date].push(fixture);
                      return acc;
                    }, {});

                    return Object.entries(fixturesByDateTeam).map(([date, dateFixtures]) => (
                      <div key={date}>
                        {/* Date header - subtle and raised above fixtures */}
                        <div className="px-4 py-2 bg-gray-50 border-b border-ui-border text-sm text-content-secondary font-medium">
                          {date}
                        </div>
                        {dateFixtures.map((fixture) => {
                          const time = fixture.kickoff_time
                            ? new Date(fixture.kickoff_time).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "TBD";

                          return (
                            <div
                              key={fixture.id}
                              className="flex items-center p-4 border-b border-ui-border last:border-b-0"
                            >
                              {/* Match details - perfectly centered to device */}
                              <div className="w-full flex items-center justify-center">
                                <div className="flex items-center justify-between w-full max-w-sm">
                                  <span className={`font-semibold text-base text-right flex-1 ${fixture.home_team === selectedTeam ? "text-ui-green" : "text-content-primary"}`}>
                                    {fixture.home_team}
                                  </span>
                                  <div className={`px-2 py-1 rounded text-sm font-medium mx-4 min-w-[50px] text-center flex-shrink-0 ${
                                    fixture.home_score !== null && fixture.away_score !== null
                                      ? 'bg-gray-400 text-white'
                                      : 'text-black'
                                  }`}>
                                    {fixture.status === 'abandoned' ? 'Ab'
                                      : fixture.status === 'postponed' ? 'Po'
                                      : (fixture.home_score !== null && fixture.away_score !== null) ? (
                                      `${fixture.home_score} - ${fixture.away_score}`
                                    ) : (
                                      time
                                    )}
                                  </div>
                                  <span className={`font-semibold text-base text-left flex-1 ${fixture.away_team === selectedTeam ? "text-ui-green" : "text-content-primary"}`}>
                                    {fixture.away_team}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ));
                  })()}
                </FcCard>
            ) : (
                // Matchday view: grouped by date
                Object.entries(fixturesByDate).map(([date, dayFixtures]) => (
                    <div key={date} className="mb-6">
                        <div className="font-bold text-lg text-brand-text mb-2 text-center w-full">{date}</div>
                        <div className="flex flex-col gap-3">
                            {dayFixtures.map((fixture) => {
                                const kickoff = fixture.kickoff_time
                                    ? new Date(fixture.kickoff_time).toLocaleTimeString([], {
                                          hour: "2-digit",
                                          minute: "2-digit",
                                      })
                                    : "";
                                return (
                                    <FcCard as="div" padding="standard"
                                        key={fixture.id}
                                        className="bg-surface-card rounded-lg shadow p-4 border border-ui-border"
                                    >
                                        {/* Match details - perfectly centered to device */}
                                        <div className="w-full flex items-center justify-center">
                                            <div className="flex items-center justify-between w-full max-w-sm">
                                                <span className="font-semibold text-base text-right flex-1 text-content-primary">
                                                    {fixture.home_team}
                                                </span>
                                                <div className={`px-2 py-1 rounded text-sm font-medium mx-4 min-w-[50px] text-center flex-shrink-0 ${
                                                    fixture.home_score !== null && fixture.away_score !== null
                                                        ? 'bg-gray-400 text-white'
                                                        : 'text-black'
                                                }`}>
                                                    {fixture.status === 'abandoned' ? 'Ab'
                                                      : fixture.status === 'postponed' ? 'Po'
                                                      : (fixture.home_score !== null && fixture.away_score !== null) ? (
                                                        `${fixture.home_score} - ${fixture.away_score}`
                                                    ) : (
                                                        kickoff
                                                    )}
                                                </div>
                                                <span className="font-semibold text-base text-left flex-1 text-content-primary">
                                                    {fixture.away_team}
                                                </span>
                                            </div>
                                        </div>
                                    </FcCard>
                                );
                            })}
                        </div>
                    </div>
                ))
            )}
          </div>
        )}
        {activeTab === "my-selections" && (
          <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow p-4 mb-6">
            <h3 className="text-xl font-bold mb-4">My Selections</h3>

            {/* Multi-entry: choose which entry's selections to view */}
            {myEntries.length > 1 && (
                <EntrySelector className="mb-4" aria-label="Entry:"
                  value={activeEntryId || ''}
                  onChange={(e) => handleActiveEntryChange(e.target.value)}
                >
                  {myEntries.map((e) => (
                    <option key={e.id} value={e.id}>{myEntryName(e)}</option>
                  ))}
                </EntrySelector>
            )}

            {/* Remaining Teams Section - Only show if there are remaining teams */}
            {remainingTeams.length > 0 && (
              <div className="mb-6 border border-ui-border rounded-lg overflow-hidden">
                <FcButton variant="tertiary" type="submit"
                  onClick={() => setShowRemainingTeams(!showRemainingTeams)}
                  className="w-full flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base font-semibold text-content-primary">
                      Teams You Haven't Selected Yet
                    </span>
                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-action-primary text-white">
                      {remainingTeams.length}
                    </span>
                  </div>
                  <svg
                    className={`w-5 h-5 text-content-secondary transition-transform ${showRemainingTeams ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </FcButton>
                {showRemainingTeams && (
                  <div className="p-4 bg-surface-card">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {remainingTeams.map((team) => (
                        <div key={team} className="text-sm text-content-primary p-2 bg-gray-50 rounded">
                          {team}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {selectionHistory.length === 0 ? (
              <div className="text-center py-8 text-content-secondary">
                <p>No selections made yet for this competition.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {selectionHistory.map((selection) => {
                  const fixture = selection.fixtures;
                  if (!fixture) return null;
                  
                  const kickoffDate = new Date(fixture.kickoff_time);
                  const dayName = kickoffDate.toLocaleDateString('en-GB', { weekday: 'long' });
                  const dateStr = kickoffDate.toLocaleDateString('en-GB', { 
                    day: '2-digit', 
                    month: 'long', 
                    year: 'numeric' 
                  });
                  const timeStr = kickoffDate.toLocaleTimeString('en-GB', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    hour12: false 
                  });

                  // Show result if match is complete, otherwise show time
                  const centerDisplay = fixture.status === 'abandoned' ? 'Ab' 
                    : fixture.status === 'postponed' ? 'Po'
                    : (fixture.status === 'complete' && fixture.home_score !== null && fixture.away_score !== null)
                    ? `${fixture.home_score} - ${fixture.away_score}`
                    : timeStr;

                  return (
                    <div key={selection.id} className="border-b border-ui-border pb-4 last:border-b-0">
                      {/* Chip badge if used */}
                      {selection.used_last_chance_chip && (
                        <div className="flex justify-end mb-1">
                          <span className="px-2 py-0.5 rounded text-xs font-semibold bg-action-primary text-white">
                            🎫 Last Chance Chip
                          </span>
                        </div>
                      )}
                      <div className="text-sm font-medium text-content-primary mb-2">
                        {dayName} {dateStr}
                      </div>
                      <div className="flex items-center justify-between text-center">
                        <div className={`flex-1 font-medium ${
                          selection.selected_team === fixture.home_team ? "text-action-primary font-semibold" : "text-content-primary"
                        }`}>
                          {fixture.home_team}
                        </div>
                        <div className="px-4 text-sm font-medium text-content-secondary">
                          {centerDisplay}
                        </div>
                        <div className={`flex-1 font-medium ${
                          selection.selected_team === fixture.away_team ? "text-action-primary font-semibold" : "text-content-primary"
                        }`}>
                          {fixture.away_team}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </FcCard>
        )}
        {activeTab === "match-days" && canManage && competition?.is_multi_league && (
          <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow p-4 mb-6">
            <h3 className="text-xl font-bold mb-1">Match Days</h3>
            <p className="text-sm text-content-secondary mb-4">
              Set how many rounds to play and which league matchday feeds each round. The number of
              rounds can be changed while registration is open; upcoming rounds&apos; matchdays can be
              changed any time before they start.
            </p>

            {!roundEditorLoaded ? (
              <LoadingState label="Loading…" />
            ) : (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-content-primary mb-1">Number of rounds</label>
                  <div className="flex items-center gap-3">
                    <FcInput aria-label="length"
                      type="number"
                      min={1}
                      max={60}
                      value={roundEditor.rounds.length}
                      onChange={(e) => setEditorNumRounds(e.target.value)}
                      disabled={!registrationOpen || roundEditorBusy}
                      className="w-24 border"
                    />
                    {registrationOpen && roundEditor.rounds.length > 1 && (
                      <FcButton variant="tertiary"
                        type="button"
                        onClick={() => { setRemoveRoundsSelected(new Set()); setShowRemoveRoundsModal(true); }}
                        disabled={roundEditorBusy}
                        className="hover:underline"
                      >
                        Remove rounds…
                      </FcButton>
                    )}
                    {!registrationOpen && (
                      <span className="text-xs text-content-secondary">Locked (registration closed)</span>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  {roundEditor.rounds.map((roundMap, idx) => {
                    const editable = isRoundEditable(idx);
                    const roundNo = idx + 1;
                    const badge = roundNo < editorCurrentRound
                      ? { label: 'Played', cls: 'text-gray-600 bg-gray-200' }
                      : roundNo === editorCurrentRound
                      ? { label: 'Current', cls: 'text-[#BA6830] bg-orange-100' }
                      : { label: 'Upcoming', cls: 'text-green-700 bg-green-100' };
                    return (
                      <div key={idx} className="border rounded p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-sm" style={{ color: '#284239' }}>Round {roundNo}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${badge.cls}`}>{badge.label}</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {roundEditor.leagues.map((lg) => {
                            const floor = editorMinMatchday(idx, lg);
                            // Only later matchdays than the previous round's pick; always
                            // keep this round's own saved value visible.
                            const mds = (leagueMatchdays[lg] || []).filter(
                              (md) => md.match_day > floor || md.match_day === roundMap[lg]
                            );
                            return (
                              <label key={lg} className="text-sm">
                                <span className="block text-content-secondary mb-0.5">{lg}</span>
                                <FcSelect aria-label="— skip —"
                                  value={roundMap[lg] ?? ''}
                                  onChange={(e) => setEditorRoundMatchday(idx, lg, e.target.value)}
                                  disabled={!editable || roundEditorBusy}
                                  className="w-full border"
                                >
                                  <option value="">— skip —</option>
                                  {mds.map((md) => (
                                    <option key={md.match_day} value={md.match_day}>
                                      MD{md.match_day}
                                      {md.first_kickoff_time ? ` · ${new Date(md.first_kickoff_time).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}` : ''}
                                    </option>
                                  ))}
                                </FcSelect>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4">
                  <FcButton variant="tertiary"
                    type="button"
                    onClick={saveRoundEditor}
                    disabled={roundEditorBusy}
                    className=""
                  >
                    {roundEditorBusy ? 'Saving…' : 'Save match days'}
                  </FcButton>
                </div>
              </>
            )}

            {/* Remove-rounds modal */}
            {showRemoveRoundsModal && (
              <FcDialog open title="Remove rounds" size="standard" onCancel={() => { setShowRemoveRoundsModal(false); setRemoveRoundsSelected(new Set()); }}>
                <FcCard as="div" padding="none" className="bg-surface-card rounded-lg shadow-xl w-full max-w-md max-h-[85vh] flex flex-col">
                  <div className="p-4 border-b">

                    <p className="text-sm text-content-secondary mt-1">
                      Select the rounds to remove. Their match-day selections are deleted when you save
                      and can&apos;t be restored. Remaining rounds are renumbered.
                    </p>
                  </div>
                  <div className="p-4 overflow-y-auto space-y-2">
                    {roundEditor.rounds.map((roundMap, idx) => {
                      const removable = isRoundEditable(idx);
                      return (
                        <label
                          key={idx}
                          className={`flex items-start gap-2 p-2 border rounded ${removable ? 'cursor-pointer hover:bg-gray-50' : 'opacity-60'}`}
                        >
                          <FcInput aria-label="Round"
                            type="checkbox"
                            checked={removeRoundsSelected.has(idx)}
                            onChange={() => removable && toggleRemoveRound(idx)}
                            disabled={!removable}
                            className="w-4 mt-0.5"
                          />
                          <span className="text-sm">
                            <span className="font-semibold text-content-primary">Round {idx + 1}</span>
                            {!removable && <span className="ml-2 text-xs text-content-secondary">(locked)</span>}
                            <span className="block text-content-secondary">{editorRoundSummary(roundMap)}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="p-4 border-t flex items-center justify-between">
                    {roundEditor.rounds.length - removeRoundsSelected.size < 1 ? (
                      <span className="text-xs text-ui-error">At least one round must remain.</span>
                    ) : (
                      <span className="text-xs text-content-secondary">
                        {removeRoundsSelected.size} of {roundEditor.rounds.length} selected
                      </span>
                    )}
                    <div className="flex gap-2">
                      <FcButton variant="secondary"
                        type="button"
                        onClick={() => { setShowRemoveRoundsModal(false); setRemoveRoundsSelected(new Set()); }}
                        className="border"
                      >
                        Cancel
                      </FcButton>
                      <FcButton variant="destructive"
                        type="button"
                        onClick={applyRemoveRounds}
                        disabled={removeRoundsSelected.size === 0 || roundEditor.rounds.length - removeRoundsSelected.size < 1}
                        className=""
                      >
                        Remove {removeRoundsSelected.size || ""} round{removeRoundsSelected.size === 1 ? "" : "s"}
                      </FcButton>
                    </div>
                  </div>
                </FcCard>
              </FcDialog>
            )}
          </FcCard>
        )}
        {activeTab === "manage-entries" && canManage && (
          <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow p-4 mb-6">
            <h3 className="text-xl font-bold mb-1">Manage Entries</h3>
            <p className="text-sm text-content-secondary mb-4">
              Approve, reject or remove entries, and mark entrants as confirmed. Footy Coach does not handle payments. “Confirmed” is just a label you control (e.g. an entrant who has paid an external fee).
            </p>

            {/* Require-approval toggle */}
            <label className="flex items-center gap-2 mb-4 text-sm font-medium text-brand-text">
              <FcInput aria-label="Require my approval before a new entry becomes active"
                type="checkbox"
                checked={!!competition.requires_approval}
                disabled={requiresApprovalBusy}
                onChange={(e) => handleToggleRequiresApproval(e.target.checked)}
                className="w-4"
              />
              Require my approval before a new entry becomes active
            </label>

            {/* Confirmed filter */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-sm text-content-secondary">Show:</span>
              <SegmentedControl label="Entry confirmation filter" value={entriesConfirmedFilter} onValueChange={setEntriesConfirmedFilter}
                options={[{value:'all',label:'All'},{value:'confirmed',label:'Confirmed'},{value:'unconfirmed',label:'Unconfirmed'}]}/>
            </div>

            {(() => {
              const visibleEntries = manageEntries.filter((e) =>
                entriesConfirmedFilter === 'confirmed' ? !!e.confirmed
                : entriesConfirmedFilter === 'unconfirmed' ? !e.confirmed
                : true
              );
              return manageEntries.length === 0 ? (
              <div className="text-sm text-content-secondary py-4">No entries yet.</div>
            ) : visibleEntries.length === 0 ? (
              <div className="text-sm text-content-secondary py-4">No {entriesConfirmedFilter} entries.</div>
            ) : (
              <div className="overflow-x-auto">
                <FcTable label="Competition View table 2" className="w-full text-left border-separate" style={{ borderSpacing: 0 }}>
                  <thead className="bg-ui-green text-white">
                    <tr>
                      <th scope="col" className="p-2 font-semibold">Entrant</th>
                      <th scope="col" className="p-2 font-semibold">Status</th>
                      <th scope="col" className="p-2 font-semibold text-center">Confirmed</th>
                      <th scope="col" className="p-2 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleEntries.map((e) => {
                      const badge =
                        e.status === 'pending' ? 'text-amber-700 bg-amber-100'
                        : e.status === 'rejected' ? 'text-red-700 bg-red-100'
                        : e.status === 'eliminated' ? 'text-gray-600 bg-gray-200'
                        : 'text-green-700 bg-green-100';
                      return (
                        <tr key={e.id} className="border-b">
                          <td className="p-2">
                            <span className="font-medium text-brand-text">{e.username}</span>
                            <span className="ml-2 text-xs text-content-secondary">
                              {e.entry_label || `Entry ${e.entry_index || 1}`}
                            </span>
                          </td>
                          <td className="p-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold capitalize ${badge}`}>{e.status || 'active'}</span>
                          </td>
                          <td className="p-2 text-center">
                            <FcInput aria-label="Enable option"
                              type="checkbox"
                              checked={!!e.confirmed}
                              disabled={entryActionBusy}
                              onChange={(ev) => handleToggleConfirmed(e.id, ev.target.checked)}
                              className="w-4"
                            />
                          </td>
                          <td className="p-2">
                            <div className="flex items-center justify-end gap-2">
                              {(e.status === 'pending' || e.status === 'rejected') && (
                                <FcButton variant="secondary" size="compact"
                                  type="button"
                                  onClick={() => handleApproveEntry(e.id)}
                                  disabled={entryActionBusy}
                                  className="border"
                                >
                                  Approve
                                </FcButton>
                              )}
                              {e.status === 'pending' && (
                                <FcButton variant="secondary" size="compact"
                                  type="button"
                                  onClick={() => handleRejectEntry(e.id)}
                                  disabled={entryActionBusy}
                                  className="text-amber-700 border border-amber-300"
                                >
                                  Reject
                                </FcButton>
                              )}
                              <FcButton variant="destructive-secondary" size="compact"
                                type="button"
                                onClick={() => handleRemoveEntry(e.id)}
                                disabled={entryActionBusy}
                                className="border"
                              >
                                Remove
                              </FcButton>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </FcTable>
              </div>
            );
            })()}
          </FcCard>
        )}
        {activeTab === "manage-selections" && (
          <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow p-4 mb-6">
            <h3 className="text-xl font-bold mb-4">Manage Selections</h3>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setManageLoading(true);
                setManageError("");
                setManageSuccess("");
                try {
                  // Find the fixture for the current matchday where the selected team is home or away
                  const fixture = fixtures.find(f =>
                    f.match_day === competition.current_match_day &&
                    (f.home_team === manageTeam || f.away_team === manageTeam)
                  );
                  if (!fixture) {
                    setManageError("No fixture found for selected team on this matchday.");
                    setManageLoading(false);
                    return;
                  }
                  const isHome = fixture.home_team === manageTeam;
                  const prediction = isHome ? "home_win" : "away_win";
                  // manageEntryId now holds the ENTRY id; resolve its owner for user_id.
                  const managedPlayer = registeredPlayers.find((p) => p.entry_id === manageEntryId);
                  const upsertPayload = {
                    user_id: managedPlayer?.user_id,
                    entry_id: manageEntryId,
                    competition_id: competition.id,
                    selected_team: manageTeam,
                    match_day: competition.current_match_day,
                    fixture_id: fixture.id,
                    prediction,
                    selection_number: 1, // Admin selections are always primary (1)
                  };
                  // Call Supabase to insert/update selection (unique per entry/matchday/slot)
                  const { error } = await supabase
                    .from("team_selections")
                    .upsert(upsertPayload, { onConflict: ["entry_id", "match_day", "selection_number"] });
                  if (error) {
                    setManageError(error.message);
                  } else {
                    setManageSuccess("Selection updated successfully.");
                  }
                } catch (err) {
                  setManageError("Failed to update selection.");
                }
                setManageLoading(false);
              }}
              className="space-y-4"
            >
              <div>
                <label htmlFor="user" className="block text-sm font-medium text-content-primary">
                  Select User
                </label>
                <FcSelect
                  id="user"
                  value={manageEntryId}
                  onChange={e => setManageEntryId(e.target.value)}
                  className="mt-1 block w-full border"
                  required
                >
                  {registeredPlayers.map((player) => (
                    <option key={player.entry_id || player.user_id} value={player.entry_id}>
                      {displayName(player)}
                    </option>
                  ))}
                </FcSelect>
              </div>
              <div>
                <label htmlFor="team" className="block text-sm font-medium text-content-primary">
                  Select Team
                </label>
                <FcSelect
                  id="team"
                  value={manageTeam}
                  onChange={e => setManageTeam(e.target.value)}
                  className="mt-1 block w-full border"
                  required
                >
                  {availableTeams.map((team) => (
                    <option key={team} value={team}>
                      {team}
                    </option>
                  ))}
                </FcSelect>
              </div>
              {manageError && <div className="text-ui-error text-sm">{manageError}</div>}
              {manageSuccess && <div className="text-ui-success text-sm">{manageSuccess}</div>}
              <FcButton variant="tertiary"
                type="submit"
                className=""
                disabled={manageLoading}
              >
                {manageLoading ? "Submitting..." : "Submit Selection"}
              </FcButton>
            </form>
          </FcCard>
        )}
        {activeTab === "admins" && canManageAdmins && (
          <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow p-4 mb-6">
            <h3 className="text-xl font-bold mb-1">Administrators</h3>
            <p className="text-sm text-content-secondary mb-4">
              Administrators can help manage this competition (manage selections, entries and settings) but cannot delete it or change administrators. Only you, the owner, can.
            </p>

            {/* Owner */}
            <div className="mb-4">
              <div className="text-xs font-semibold text-content-secondary uppercase mb-1">Owner</div>
              <div className="flex items-center justify-between bg-gray-50 border border-ui-border rounded px-3 py-2">
                <span className="font-semibold text-brand-text">{competition.created_by_username || 'Owner'}</span>
                <span className="text-xs font-semibold text-action-primary">Owner</span>
              </div>
            </div>

            {/* Admin list */}
            <div className="mb-4">
              <div className="text-xs font-semibold text-content-secondary uppercase mb-1">Administrators</div>
              {adminRoster.length === 0 ? (
                <div className="text-sm text-content-secondary py-2">No administrators yet.</div>
              ) : (
                <div className="border border-ui-border rounded divide-y">
                  {adminRoster.map((a) => (
                    <div key={a.user_id} className="flex items-center justify-between px-3 py-2">
                      <span className="font-medium text-brand-text">
                        {a.username}
                        {a.user_id === session?.user?.id && <span className="ml-2 text-xs text-content-secondary">(you)</span>}
                      </span>
                      {a.user_id === session?.user?.id ? (
                        <span className="text-xs text-content-secondary">—</span>
                      ) : (
                        <FcButton variant="destructive-secondary" size="compact"
                          type="button"
                          onClick={() => handleRemoveAdmin(a.user_id)}
                          disabled={adminActionBusy}
                          className="border"
                        >
                          Remove
                        </FcButton>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add admin — chosen from entrants */}
            <form onSubmit={handleAddAdmin} className="space-y-2">
              <label className="block text-sm font-medium text-content-primary">Add an administrator from the entrants</label>
              {adminCandidates.length === 0 ? (
                <div className="text-sm text-content-secondary">
                  No eligible entrants to add. Players must have entered this competition before they can be made administrators.
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2">
                  <FcSelect aria-label="Select a player…"
                    value={newAdminUserId}
                    onChange={(e) => { setNewAdminUserId(e.target.value); setAdminError(""); }}
                    className="flex-1 border"
                  >
                    <option value="">Select a player…</option>
                    {adminCandidates.map((c) => (
                      <option key={c.user_id} value={c.user_id}>{c.username}</option>
                    ))}
                  </FcSelect>
                  <FcButton variant="tertiary"
                    type="submit"
                    disabled={adminActionBusy || !newAdminUserId}
                    className="disabled:cursor-not-allowed"
                  >
                    {adminActionBusy ? 'Working…' : 'Add Admin'}
                  </FcButton>
                </div>
              )}
              {adminError && <div className="text-ui-error text-sm">{adminError}</div>}
            </form>
          </FcCard>
        )}
        {activeTab === "registered-players" && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <div className="font-bold text-lg text-brand-text">Registered Players</div>
              {/* Invite Squad Button - Only visible to creator of private competitions */}
              {competition.visibility === "private" && canManage && (
                <FcButton variant="tertiary" type="submit"
                  onClick={async () => {
                    await fetchUserSquads();
                    setShowInviteSquadModal(true);
                  }}
                  className="hover:underline focus:underline"
                  style={{ color: '#BA6830' }}
                >
                  Invite Squad
                </FcButton>
              )}
            </div>
            <FcCard as="div" padding="none" className="bg-surface-card rounded-lg shadow border border-ui-border overflow-x-auto">
              <FcTable label="Competition View table 3" className="w-full text-left border-separate" style={{ borderSpacing: 0 }}>
                <thead className="bg-ui-green text-white">
                  <tr>
                    <th scope="col" className="p-2 font-semibold border-r border-white w-1/2">Player Name</th>
                    <th scope="col" className="p-2 font-semibold w-1/2">Team Selection <span title="Selections will be revealed when no further selections can be made." className="ml-2 text-xs text-gray-200">ⓘ</span></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRegisteredPlayers.map((entry, idx) => (
                    <tr key={entry.entry_id || entry.user_id || `${entry.username}-${(regPlayersPage-1)*REG_PLAYERS_PER_PAGE+idx}`}>
                      <td className="p-2">{displayName(entry)}</td>
                      <td className="p-2">{renderSelection(entry)}</td>
                    </tr>
                  ))}
                </tbody>
              </FcTable>
            </FcCard>
            {/* Pagination controls for Registered Players */}
            {registeredPlayers.length > REG_PLAYERS_PER_PAGE && (
              <div className="mt-2 flex items-center justify-between">
                <div className="text-sm text-content-secondary">Showing {(registeredPlayers.length === 0) ? 0 : ((regPlayersPage-1) * REG_PLAYERS_PER_PAGE + 1)} - {Math.min(regPlayersPage * REG_PLAYERS_PER_PAGE, registeredPlayers.length)} of {registeredPlayers.length} players</div>
                <div className="flex-shrink-0">
                  <FcButton variant="secondary" size="compact" type="submit"
                    onClick={() => setRegPageSafe(regPlayersPage - 1)}
                    className=""
                    disabled={regPlayersPage === 1}
                  >
                    &lt; Prev
                  </FcButton>
                  <FcButton variant="secondary" size="compact" type="submit"
                    onClick={() => setRegPageSafe(regPlayersPage + 1)}
                    className=""
                    disabled={regPlayersPage === totalRegisteredPages}
                  >
                    Next &gt;
                  </FcButton>
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === "standings" && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <div className="font-bold text-lg text-brand-text">Competition Standings</div>
              <div className="flex items-center gap-3">
                <Link
                  to={competition.type === "season" ? `/season-leaderboard/${competitionId}` : competition.type === 'world_stage' ? `/world-stage/${competitionId}?tab=leaderboard` : `/leaderboard/${competitionId}`}
                  className="text-action-primary font-semibold text-sm hover:underline"
                >
                  View All
                </Link>
                {competition.type === "season" && currentUserEntry?.rank && (
                  <ShareButton
                    url={`${window.location.origin}/competition/${competitionId}`}
                    title={`My ranking in ${competition.name}`}
                    text={`I'm ranked #${currentUserEntry.rank} in ${competition.name}! 🏆`}
                  >
                    Share Rank
                  </ShareButton>
                )}
              </div>
            </div>
            {competition.type === 'season' && competition.top_gaffer_enabled && topGafferStages.length > 0 && (
              <div className="mb-3 flex items-center gap-2 flex-wrap">
                <label className="text-sm font-semibold text-ui-green">Standings:</label>
                <FcSelect aria-label="Standings:"
                  value={selectedStageId || ''}
                  onChange={(e) => { setSelectedStageId(e.target.value || null); setStandingsPage(1); }}
                  className="border"
                >
                  <option value="">Full Season</option>
                  {topGafferStages.map((s) => (
                    <option key={s.stage_id} value={s.stage_id}>
                      Top Gaffer — Stage {s.stage_number}
                      {s.status === 'in_progress' ? ' • current' : s.status === 'complete' ? ' • complete' : ' • upcoming'}
                    </option>
                  ))}
                </FcSelect>
                {selectedStage && (
                  <span className="text-xs text-content-secondary">
                    MD{selectedStage.first_matchday}–MD{selectedStage.last_matchday}
                  </span>
                )}
              </div>
            )}
            {competition.type === 'world_stage' && (
              <div className="mb-3 p-3 rounded border border-ui-border bg-[#FFF8F2] text-sm text-[#7a4a22] flex items-center justify-between gap-2">
                <span>World Stage uses a dedicated leaderboard and prediction interface.</span>
                <Link
                  to={`/world-stage/${competitionId}?tab=leaderboard`}
                  className="text-action-primary font-semibold hover:underline"
                >
                  Open World Stage
                </Link>
              </div>
            )}
            {isUpcomingStage ? (
              <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow border border-ui-border p-6 text-center">
                <div className="text-ui-green font-semibold mb-1">This Top Gaffer stage hasn&apos;t started yet.</div>
                <div className="text-sm text-content-secondary">
                  Stage {selectedStage.stage_number} runs MD{selectedStage.first_matchday}–MD{selectedStage.last_matchday}. Everyone starts level — a fresh chance to top the table.
                </div>
              </FcCard>
            ) : (
            <FcCard as="div" padding="none" className="bg-surface-card rounded-lg shadow border border-ui-border overflow-x-auto">
              {/* Desktop/tablet view */}
              <FcTable label="Competition View table 4" className="w-full text-left table-fixed border-separate hidden md:table" style={{ borderSpacing: 0 }}>
                <thead className="bg-ui-green text-white">
                  {competition.type === "season" ? (
                    <tr>
                      <th scope="col" className="p-2 font-semibold w-[7%]">Rank</th>
                      <th scope="col" className="p-2 font-semibold w-[22%]">Player</th>
                      <th scope="col" className="p-2 font-semibold text-center w-[9%]">Wins</th>
                      <th scope="col" className="p-2 font-semibold text-center w-[9%]">Draws</th>
                      <th scope="col" className="p-2 font-semibold text-center w-[9%]">Losses</th>
                      <th scope="col" className="p-2 font-semibold text-center w-[9%]">GF</th>
                      <th scope="col" className="p-2 font-semibold text-center w-[9%]">GA</th>
                      <th scope="col" className="p-2 font-semibold text-center w-[16%]">Points</th>
                    </tr>
                  ) : (
                    <tr>
                      <th scope="col" className="p-2 font-semibold">Rank</th>
                      <th scope="col" className="p-2 font-semibold">Player</th>
                      <th scope="col" className="p-2 font-semibold">Status</th>
                      <th scope="col" className="p-2 font-semibold">Elimination Matchday</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {competition.type === "season"
                    ? paginatedStandings.map((entry, idx) => (
                        <tr key={entry.entry_id || entry.user_id || `${entry.username}-${(standingsPage-1)*STANDINGS_PER_PAGE+idx}`}
                            className="">
                          <td className="p-2">{entry.rank}</td>
                          <td className="p-2">{displayName(entry)}</td>
                          <td className="p-2 text-center">{entry.wins}</td>
                          <td className="p-2 text-center">{entry.draws}</td>
                          <td className="p-2 text-center">{entry.losses}</td>
                          <td className="p-2 text-center">{entry.gf}</td>
                          <td className="p-2 text-center">{entry.ga}</td>
                          <td className="p-2 text-center font-bold">{entry.points}</td>
                        </tr>
                      ))
                    : paginatedStandings.map((entry, idx) => (
                        <tr key={entry.entry_id || entry.user_id || `${entry.username}-${(standingsPage-1)*STANDINGS_PER_PAGE+idx}`}
                            className="">
                          <td className="p-2">{entry.rank}</td>
                          <td className="p-2">{displayName(entry)}</td>
                          <td className="p-2">{statusLabel(entry.status)}</td>
                          <td className="p-2">{eliminationMatchday(entry)}</td>
                        </tr>
                      ))}
                </tbody>
              </FcTable>
              {/* Mobile view */}
              <FcTable label="Competition View table 5" className="w-full text-left table-fixed border-separate md:hidden" style={{ borderSpacing: 0 }}>
                <thead className="bg-ui-green text-white">
                  {competition.type === "season" ? (
                    <tr>
                      <th scope="col" className="p-2 font-semibold w-[20%]">Rank</th>
                      <th scope="col" className="p-2 font-semibold w-[55%]">Player</th>
                      <th scope="col" className="p-2 font-semibold text-center w-[25%]">Points</th>
                    </tr>
                  ) : (
                    <tr>
                      <th scope="col" className="p-2 font-semibold w-[40%]">Status</th>
                      <th scope="col" className="p-2 font-semibold w-[60%]">Player</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {competition.type === "season"
                    ? paginatedStandings.map((entry, idx) => (
                        <tr key={entry.entry_id || entry.user_id || `${entry.username}-${(standingsPage-1)*STANDINGS_PER_PAGE+idx}`}
                            className="">
                          <td className="p-2">{entry.rank}{ordinalSuffix(entry.rank)}</td>
                          <td className="p-2">{displayName(entry)}</td>
                          <td className="p-2 text-center font-bold">{entry.points}</td>
                        </tr>
                      ))
                    : paginatedStandings.map((entry, idx) => (
                        <tr key={entry.entry_id || entry.user_id || `${entry.username}-${(standingsPage-1)*STANDINGS_PER_PAGE+idx}`}
                            className="">
                          <td className="p-2">{statusLabel(entry.status)}</td>
                          <td className="p-2">{displayName(entry)}</td>
                        </tr>
                      ))}
                </tbody>
              </FcTable>
            </FcCard>
            )}
            {/* Pagination controls for Standings */}
            {activeStandings.length > STANDINGS_PER_PAGE && (
              <div className="mt-2 flex items-center justify-between">
                <div className="text-sm text-content-secondary">Showing {(activeStandings.length === 0) ? 0 : ((standingsPage-1) * STANDINGS_PER_PAGE + 1)} - {Math.min(standingsPage * STANDINGS_PER_PAGE, activeStandings.length)} of {activeStandings.length} players</div>
                <div className="flex-shrink-0">
                  <FcButton variant="secondary" size="compact" type="submit"
                    onClick={() => setStandingsPageSafe(standingsPage - 1)}
                    className=""
                    disabled={standingsPage === 1}
                  >
                    &lt; Prev
                  </FcButton>
                  <FcButton variant="secondary" size="compact" type="submit"
                    onClick={() => setStandingsPageSafe(standingsPage + 1)}
                    className=""
                    disabled={standingsPage === totalStandingsPages}
                  >
                    Next &gt;
                  </FcButton>
                </div>
              </div>
            )}
          </div>
        )}
</FcTabs>
      </div>
      
      {/* How to Play Modal */}
      {showHowModal && (
        <HowToPlayModal
          isOpen={showHowModal}
          onClose={() => setShowHowModal(false)}
          competitionType={competition.type}
        />
      )}

      {/* Unregister modal: choose which entries to remove */}
      {showUnregisterModal && (
        <FcDialog open title="Unregister from" size="standard" onCancel={() => setShowUnregisterModal(false)}>
          <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-lg p-6 w-full max-w-md">

            {myEntries.length > 1 ? (
              <>
                <p className="text-sm text-content-secondary mb-3">
                  You have {myEntries.length} entries in this competition. Choose which to remove. Removing an entry also deletes its selections.
                </p>
                <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-brand-text">
                  <FcInput aria-label="All entries"
                    type="checkbox"
                    checked={unregisterSelectedIds.length === myEntries.length}
                    onChange={(e) =>
                      setUnregisterSelectedIds(e.target.checked ? myEntries.map((x) => x.id) : [])
                    }
                    className="w-4"
                  />
                  All entries
                </label>
                <div className="border border-ui-border rounded divide-y max-h-56 overflow-y-auto mb-4">
                  {myEntries.map((e) => (
                    <label key={e.id} className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-gray-50">
                      <FcInput aria-label="Enable option"
                        type="checkbox"
                        checked={unregisterSelectedIds.includes(e.id)}
                        onChange={() => toggleUnregisterEntry(e.id)}
                        className="w-4"
                      />
                      {myEntryName(e)}
                    </label>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-content-secondary mb-4">
                This removes your entry and its selections from the competition. This cannot be undone.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <FcButton variant="tertiary"
                type="button"
                onClick={() => setShowUnregisterModal(false)}
                className=""
              >
                Cancel
              </FcButton>
              <FcButton variant="tertiary"
                type="button"
                onClick={handleUnregisterEntries}
                disabled={unregistering || unregisterSelectedIds.length === 0}
                className="disabled:cursor-not-allowed"
              >
                {unregistering
                  ? 'Removing…'
                  : (myEntries.length > 1
                      ? `Remove ${unregisterSelectedIds.length} ${unregisterSelectedIds.length === 1 ? 'entry' : 'entries'}`
                      : 'Unregister')}
              </FcButton>
            </div>
          </FcCard>
        </FcDialog>
      )}

      {/* Registration Key Modal for Private Competitions */}
      {showKeyModal && pendingComp && (
        <FcDialog open title="Enter Registration Key" size="standard" onCancel={() => {
                     setShowKeyModal(false);
                     setKeyInput("");
                     setKeyError("");
                     setPendingComp(null);
                   }}>
          <div className="bg-brand-white rounded-lg shadow-lg p-6 w-80">

            <form onSubmit={handlePrivateJoin} className="space-y-4">
               <FcInput aria-label="Registration Key"
                 type="text"
                 placeholder="Registration Key"
                 value={keyInput}
                 onChange={e => setKeyInput(e.target.value)}
                 className="block w-full border"
                 required
               />
               {keyError && <div className="text-ui-error text-sm">{keyError}</div>}
               <div className="flex gap-2 justify-end">
                 <FcButton variant="tertiary"
                   type="button"
                   className=""
                   onClick={() => {
                     setShowKeyModal(false);
                     setKeyInput("");
                     setKeyError("");
                     setPendingComp(null);
                   }}
                 >
                   Cancel
                 </FcButton>
                 <FcButton variant="primary"
                   type="submit"
                   className=""
                 >
                   Join
                 </FcButton>
               </div>
             </form>
          </div>
        </FcDialog>
      )}

      {/* Invite Squad Modal */}
      {showInviteSquadModal && (
        <FcDialog open title="Invite Squad Members" size="standard" onCancel={() => {
                  setShowInviteSquadModal(false);
                  setSelectedSquadsToInvite([]);
                }} cancelDisabled={sendingInvites}>
          <div className="bg-brand-white rounded-lg shadow-lg p-6 w-96 max-h-[80vh] overflow-y-auto">

            
            {userSquads.length === 0 ? (
              <p className="text-content-secondary mb-4">You don't have any squads yet.</p>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-content-secondary mb-3">
                  Select squads to invite to this competition:
                </p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {userSquads.map(squad => (
                    <label key={squad.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                      <FcInput aria-label="( members)"
                        type="checkbox"
                        checked={selectedSquadsToInvite.includes(squad.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSquadsToInvite([...selectedSquadsToInvite, squad.id]);
                          } else {
                            setSelectedSquadsToInvite(selectedSquadsToInvite.filter(id => id !== squad.id));
                          }
                        }}
                        className=""
                      />
                      <span className="font-medium">{squad.name}</span>
                      <span className="text-sm text-content-secondary">({squad.member_count} members)</span>
                    </label>
                  ))}
                </div>
                {selectedSquadsToInvite.length > 0 && (
                  <p className="text-sm text-content-secondary mt-2">
                    {selectedSquadsToInvite.length} squad{selectedSquadsToInvite.length !== 1 ? 's' : ''} selected
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-2 justify-end mt-6">
              <FcButton variant="tertiary"
                type="button"
                className=""
                onClick={() => {
                  setShowInviteSquadModal(false);
                  setSelectedSquadsToInvite([]);
                }}
                disabled={sendingInvites}
              >
                Cancel
              </FcButton>
              <FcButton variant="primary"
                type="button"
                className="disabled:cursor-not-allowed"
                onClick={handleSendSquadInvitations}
                disabled={sendingInvites || selectedSquadsToInvite.length === 0}
              >
                {sendingInvites ? 'Sending...' : 'Send Invitations'}
              </FcButton>
            </div>
          </div>
        </FcDialog>
      )}
    </div>
  );
}
